import { Show } from "@refinedev/antd";
import { useShow, useGetIdentity } from "@refinedev/core";
import { useNavigate, useLocation } from "react-router";
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Empty,
  Popconfirm,
  message,
  notification,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  App,
  Breadcrumb,
  Dropdown,
  Row,
  Col,
  Tabs,
  Badge,
  Tag,
  Spin,
  Steps,
  Typography,
  Alert,
  Tooltip,
  Segmented,
} from "antd";
import type { MenuProps } from "antd";
import {
  EnvironmentOutlined,
  FilePdfOutlined,
  UploadOutlined,
  CloudDownloadOutlined,
  DownOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  MergeCellsOutlined,
  CameraOutlined,
  PictureOutlined,
  TeamOutlined,
  HomeOutlined,
  AuditOutlined,
  WarningOutlined,
  AlertOutlined,
  BlockOutlined,
  ExperimentOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ThunderboltOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  FileExcelOutlined,
  FundOutlined,
  LoadingOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useMemo } from "react";
import type { AuditDetail, PlotVisited, Sample, AuditPlot } from "../../types/audit";
import { usePageTitle } from "../../hooks";
import { AuditIncidencesSection } from "./components/AuditIncidencesSection";
import { AuditPhotoGallery } from "./components/AuditPhotoGallery";
import { AuditSubmissionsPanel } from "./components/AuditSubmissionsPanel";
import { CreateIncidenceModal } from "./components/CreateIncidenceModal";
import { FarmHistoryCard } from "./components/FarmHistoryCard";
import { UploadAuditActModal } from "./components/UploadAuditActModal";
import { SampleBatchEditor } from "./components/SampleBatchEditor";
import { AforoPushCard } from "./components/AforoPushCard";
import { UploadAuditDataModal } from "./components/UploadAuditDataModal";
import { PhotoUploadModal } from "./components/PhotoUploadModal";
import { PhotoGalleryModal } from "../../components/PhotoGalleryModal";
import { ControlPointsSection } from "./components/ControlPointsSection";
import { AdjacentCropsSection } from "./components/AdjacentCropsSection";
import { ContactChainsPanel } from "./components/ContactChainsPanel";
import { AuditMapCard, type AuditMapCardHandle } from "./components/AuditMapCard";
import { AuditSummaryTab } from "./components/AuditSummaryTab";
import { SurfaceValidationCard } from "../../components/SurfaceValidationCard";
import { CancelAuditModal } from "../planning-groups/components/CancelAuditModal";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES, AUDITOR_USER_TYPES, isEurosemillasScoped } from "../../types/auth";
import {
  auditPlotService,
  controlPointService,
  adjacentCropService,
} from "../../services/api";
import { apiClient } from "../../services/api/apiClient";
import dayjs from "dayjs";
import { getMediaUrl } from "../../utils/mediaUrl";
import type { AuditReportData } from "../../utils/auditReportPdf";

// The `/open-for-farm/` endpoints (control points + adjacent crops)
// return either a flat array or a GeoJSON FeatureCollection
// depending on the request. Normalise both to the flat
// properties-only shape the PDF mapper expects.
const extractCollection = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.features)) {
    return raw.features.map((f: any) => ({
      ...(f.properties ?? {}),
      id: f.id ?? f.properties?.id,
    }));
  }
  return [];
};

// Map the audit detail (+ separately-fetched CP/AC, the loaded
// plots, and an optional captured map snapshot) into the schema the
// PDF builder consumes. Kept module-level because it has no React
// dependencies and is reused by the (future) finalise flow.
const buildAuditReportData = (params: {
  audit: AuditDetail;
  plots: AuditPlot[];
  controlPoints: any[];
  adjacentCrops: any[];
  mapCapture?: AuditReportData["map_capture"];
  samplesMapCapture?: AuditReportData["samples_map_capture"];
}): AuditReportData => {
  const {
    audit,
    plots,
    controlPoints,
    adjacentCrops,
    mapCapture,
    samplesMapCapture,
  } = params;
  const fmtDate = (d?: string | null) =>
    d ? dayjs(d).format("DD/MM/YYYY") : "—";
  const isUnderNet =
    audit.is_under_net === "yes"
      ? "Sí"
      : audit.is_under_net === "no"
        ? "No"
        : audit.is_under_net === "partial"
          ? "Parcial"
          : "—";
  const productionType =
    audit.production_type === "convencional"
      ? "Convencional"
      : audit.production_type === "transformacion"
        ? "En conversión"
        : audit.production_type === "eco"
          ? "ECO"
          : "—";

  return {
    audit_reference: audit.reference || `Auditoría #${audit.id}`,
    audit_type: audit.audit_type?.name ?? "—",
    audit_state: audit.audit_state?.name ?? "—",
    season: audit.season?.name ?? "—",
    producer_name: audit.farm?.producer?.name ?? "—",
    farm_name: audit.farm?.name ?? "—",
    auditor_name: audit.auditor_user?.name ?? "—",
    start_date: fmtDate(audit.start_date),
    start_time: audit.start_time ?? "—",
    finishing_time: audit.finishing_time ?? "—",
    harvest_date: fmtDate(audit.harvest_date),
    stimated_production:
      audit.stimated_production != null
        ? `${Number(audit.stimated_production).toLocaleString("es-ES")} kg`
        : "—",
    packers:
      audit.packers && audit.packers.length > 0
        ? audit.packers.map((p) => p.name).join(", ")
        : "—",
    exporters:
      audit.exporters && audit.exporters.length > 0
        ? audit.exporters.map((e) => e.name).join(", ")
        : "—",
    reason_for_decrease:
      audit.reason_for_decrease && audit.reason_for_decrease.length > 0
        ? audit.reason_for_decrease.map((r) => r.name).join(", ")
        : "—",
    decrease_percentage:
      audit.decrease_percentage != null
        ? `${audit.decrease_percentage} %`
        : "—",
    is_under_net: isUnderNet,
    production_type: productionType,
    comments: audit.comments ?? "",
    // Receiver in-situ contact: prefer the new unified FK (which
    // resolves to `{ id, name }`), fall back to the legacy free-text
    // `contact_receiver` string. Neither is in the AuditDetail type
    // yet so we read them via `any`.
    contact_receiver: (() => {
      const unified = (audit as any).contact_receiver_unified;
      if (unified && typeof unified === "object" && unified.name) {
        return String(unified.name);
      }
      const legacy = (audit as any).contact_receiver;
      if (legacy && String(legacy).trim()) return String(legacy);
      return "—";
    })(),
    plots: plots.map((p) => {
      const anyP = p as any;
      // Marcamos como "dada de baja en este audit" cuando la PlotReal
      // canónica está cerrada Y la cerró este mismo audit. Si la cerró
      // un audit anterior la parcela ni siquiera llega aquí (filtrada
      // por get_queryset en backend), así que el `===` es seguro.
      const closedInThisAudit =
        anyP.plot_real_status === "closed" &&
        anyP.plot_real_closed_in_audit === audit.id;
      return {
        name: p.name,
        plot_type: p.plot_type_name || "—",
        pattern: p.pattern_name || "—",
        plantation_date: p.plantation_date,
        plantation_frame_row: p.plantation_frame_row ?? null,
        plantation_frame_column: p.plantation_frame_column ?? null,
        surface: Number(p.surface) || 0,
        plants: Number(p.plants) || 0,
        intermediate_wood: p.intermediate_wood_name ?? null,
        grafting: p.grafting,
        doubled_with: p.doubled_with,
        is_illegal: !!p.is_ilegal,
        production_blocks: p.blocks?.map((b) => ({
          percentage: Number(b.percentage) || 0,
          avg_yield: Number(b.avg_yield) || 0,
        })),
        // Calibre medio plot-level (mm). Backend lo devuelve como
        // `avg_caliber_calculated`. Fallback: si la parcela viene de
        // una auditoría anterior al refactor (2026-07) y no lo tiene,
        // usamos el ponderado de los bloques como estimación.
        avg_caliber: (() => {
          const plotLevel = (anyP as any).avg_caliber_calculated;
          if (plotLevel != null && Number(plotLevel) > 0) {
            return Number(plotLevel);
          }
          const blocks = p.blocks ?? [];
          if (blocks.length === 0) return null;
          const total = blocks.reduce(
            (s, b) => s + (Number(b.percentage) || 0),
            0,
          );
          if (total <= 0) return null;
          const weighted = blocks.reduce(
            (s, b) =>
              s + (Number(b.percentage) || 0) * (Number(b.avg_caliber) || 0),
            0,
          );
          const c = weighted / total;
          return c > 0 ? c : null;
        })(),
        closed_in_this_audit: closedInThisAudit,
        close_reason: closedInThisAudit
          ? anyP.plot_real_comment_closed || null
          : null,
        close_date: closedInThisAudit
          ? anyP.plot_real_closed_date || null
          : null,
      };
    }),
    control_points: controlPoints.map((cp: any) => ({
      name: cp.name ?? "—",
      reason: cp.reason_name ?? "—",
      is_producer_owner: !!cp.is_producer_owner,
      estimated_surface:
        Number(
          cp.latest_review?.estimated_surface ?? cp.total_estimated_surface,
        ) || 0,
      comment: cp.latest_review?.comment ?? "",
    })),
    adjacent_crops: adjacentCrops.map((ac: any) => ({
      name: ac.name ?? ac.species_name ?? "—",
      species: ac.species_name ?? "—",
      variety: ac.variety_name ?? "—",
      is_producer_owner: !!ac.is_producer_owner,
      estimated_surface: Number(ac.total_estimated_surface) || 0,
      comment: ac.latest_review?.comment ?? "",
    })),
    samples: (audit.samples_summary ?? []).map((s: any) => {
      const isCp = !!s.is_control_point;
      const stateName: string = (s.state_name || "").toUpperCase();
      const result: "Tango" | "No Tango" | "Pendiente" =
        stateName.includes("NO") && stateName.includes("TANGO")
          ? "No Tango"
          : stateName.includes("TANGO")
            ? "Tango"
            : "Pendiente";
      return {
        lab_code: s.code ?? "—",
        sample_reference: s.code ?? "—",
        type: "Hoja",
        waypoint: s.waypoint ?? "—",
        plot_or_cp: isCp ? s.plot_name || "" : s.plot_name || "",
        is_control_point: isCp,
        result,
      };
    }),
    map_capture: mapCapture,
    samples_map_capture: samplesMapCapture,
    map_image_data_url: mapCapture?.dataUrl,
  };
};

// Add global styles for sample rows
const sampleRowStyles = `
  .sample-row-success td { background-color: #d4edda !important; }
  .sample-row-info td { background-color: #d1ecf1 !important; }
  .sample-row-error td { background-color: #f8d7da !important; }
  .sample-row-warning td { background-color: #fff3cd !important; }
  
  /* Override Ant Design's sorted column background */
  .sample-row-success td.ant-table-column-sort { background-color: #d4edda !important; }
  .sample-row-info td.ant-table-column-sort { background-color: #d1ecf1 !important; }
  .sample-row-error td.ant-table-column-sort { background-color: #f8d7da !important; }
  .sample-row-warning td.ant-table-column-sort { background-color: #fff3cd !important; }
`;

/**
 * AuditShow Component
 * Displays detailed information about an audit including main info, plots visited, and samples
 */
export const AuditShow = () => {
  const { t, i18n } = useTranslation();
  // `modal` already pulled for confirms across this page. We also
  // pull `message` for the closure-report flow so toasts render
  // under the App context (antd v5 warns when the static `message`
  // is used inside ConfigProvider-themed apps and the toast never
  // appears).
  const { modal, message: appMessage } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { query } = useShow<AuditDetail>({
    resource: "audits",
  });

  const { data, isLoading, refetch } = query;
  const audit = data?.data;

  // Scroll a la cabecera al entrar en una auditoría. Sin esto, navegar
  // desde la lista de auditorías de una planificación deja la página
  // anclada a la posición vertical previa (lo que mostraba la tabla en
  // ese momento), y la pantalla parece "abrirse por la mitad".
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [audit?.id]);

  // Admin gating — drives whether the contact chains panel exposes
  // direct-edit links (admin) or "Propose ..." actions (auditor /
  // coordinator route the change through a Contactos ticket).
  const { data: identity } = useGetIdentity<UserInfo>();
  // ESA (user_type 2) is read-only across the app — treat as non-admin here
  // so every downstream edit/delete gate that keys off `isAdmin` disappears
  // for them without touching each call site individually.
  const isAdmin =
    (ADMIN_USER_TYPES as readonly number[]).includes(
      identity?.app_user?.user_type ?? -1,
    ) && !isEurosemillasScoped(identity);
  // Coordinator (user_type=4) — puede fusionar y editar plantings
  // desde web como el admin, sin ser admin global.
  const isCoordinator = identity?.app_user?.user_type === 4;
  // Auditors (user_type 4/5) live under the `/auditor/...` route
  // namespace — back-navigation and breadcrumb links must point
  // there or they land on screens they aren't allowed to access.
  const isAuditor = (AUDITOR_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  // Auditor asignado a esta auditoría — habilita acciones (como
  // generar/regenerar el acta sin firma del productor) que de otra
  // forma son admin-only. El backend aplica la misma regla en
  // `generate_acta`.
  const isAssignedAuditor =
    identity?.app_user?.user_type === 5 &&
    !!audit?.auditor_user?.id &&
    audit.auditor_user.id === identity.app_user.id;
  const canGenerateActa = isAdmin || isAssignedAuditor;

  // Set page title
  usePageTitle(audit?.reference || "Auditoría");
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadDataModalVisible, setUploadDataModalVisible] = useState(false);
  // Revisión moved to a full page at
  // /audits/:id/plots/:plotId/review — see plot-review.tsx. The
  // legacy 3-step modal state hooks are gone with it.
  // "Dar de baja parcela" — small modal asking the reason, then a
  // POST that flips the canonical PlotReal's status to closed.
  const [plotCloseModalOpen, setPlotCloseModalOpen] = useState(false);
  const [plotForClose, setPlotForClose] = useState<AuditPlot | null>(null);
  const [plotCloseReason, setPlotCloseReason] = useState("");
  const [plotClosing, setPlotClosing] = useState(false);

  // Bulk close — atajo desde el aviso "No se puede finalizar todavía"
  // cuando hay varias parcelas pendientes que en realidad están
  // arrancadas. El auditor escribe un único motivo y se aplica a
  // todas en serie reusando el endpoint de close.
  const [bulkCloseModalOpen, setBulkCloseModalOpen] = useState(false);
  const [bulkClosePlots, setBulkClosePlots] = useState<AuditPlot[]>([]);
  const [bulkCloseReason, setBulkCloseReason] = useState("");
  const [bulkClosing, setBulkClosing] = useState(false);

  const handleConfirmBulkClose = async () => {
    const reason = bulkCloseReason.trim();
    if (!reason) {
      appMessage.warning(
        t("plot.close_reason_required", "Indica el motivo del cierre"),
      );
      return;
    }
    if (!audit?.id || bulkClosePlots.length === 0) return;

    setBulkClosing(true);
    const today = (await import("dayjs")).default().format("YYYY-MM-DD");
    let succeeded = 0;
    let failed = 0;
    for (const plot of bulkClosePlots) {
      try {
        await apiClient.post(`/audit-plots/${plot.id}/close/`, {
          comment_closed: reason,
          audit_id: audit.id,
          closed_date: today,
        });
        succeeded++;
      } catch {
        failed++;
      }
    }
    setBulkClosing(false);
    setBulkCloseModalOpen(false);
    setBulkCloseReason("");
    setBulkClosePlots([]);
    if (succeeded > 0) {
      appMessage.success(
        t("plot.bulk_close_success", "{{n}} parcelas dadas de baja", {
          n: succeeded,
        }),
      );
    }
    if (failed > 0) {
      appMessage.error(
        t("plot.bulk_close_partial", "{{n}} parcelas no se pudieron cerrar", {
          n: failed,
        }),
      );
    }
    refetch();
    bumpHistory();
  };

  const handleConfirmClosePlot = async () => {
    if (!plotForClose) return;
    const reason = plotCloseReason.trim();
    if (!reason) {
      appMessage.warning(
        t("plot.close_reason_required", "Indica el motivo del cierre"),
      );
      return;
    }
    setPlotClosing(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      const today = (await import("dayjs")).default().format("YYYY-MM-DD");
      await apiClient.post(`/audit-plots/${plotForClose.id}/close/`, {
        comment_closed: reason,
        audit_id: audit?.id,
        closed_date: today,
      });
      appMessage.success(
        t("plot.close_success", "Parcela dada de baja"),
      );
      setPlotCloseModalOpen(false);
      setPlotForClose(null);
      setPlotCloseReason("");
      refetch();
      bumpHistory();
    } catch (e: any) {
      appMessage.error(
        e?.response?.data?.detail ||
          e?.response?.data?.comment_closed ||
          e?.message ||
          t("plot.close_error", "No se pudo dar de baja la parcela"),
      );
    } finally {
      setPlotClosing(false);
    }
  };
  const [photoUploadModalVisible, setPhotoUploadModalVisible] = useState(false);
  const [photoGalleryModalVisible, setPhotoGalleryModalVisible] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState<{ id: string; name: string } | null>(null);
  const [auditPlots, setAuditPlots] = useState<AuditPlot[]>([]);
  const [loadingPlots, setLoadingPlots] = useState(false);
  // Modo "fusionar parcelas" — el admin/coord marca 2+ parcelas y
  // confirma. Mirror del flujo móvil en `AuditPlotsListScreen`.
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelectedIds, setMergeSelectedIds] = useState<number[]>([]);
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  
  // Sample modal states
  const [sampleModalVisible, setSampleModalVisible] = useState(false);
  // Batch editor is the default Create-samples flow now; the single
  // sample modal stays open for Edit-one (the table's edit button).
  const [batchEditorVisible, setBatchEditorVisible] = useState(false);
  // Loading flag para el botón "Exportar Excel" de la card muestras.
  const [exportingSamplesXlsx, setExportingSamplesXlsx] = useState(false);
  const exportSamplesXlsx = async () => {
    if (!audit?.id) return;
    setExportingSamplesXlsx(true);
    try {
      const res = await apiClient.get<Blob>(
        `/audits/${audit.id}/samples-xlsx/`,
        { responseType: 'blob' },
      );
      const filename = `muestras-${audit.reference || audit.id}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[audit-samples-xlsx] download failed', error);
    } finally {
      setExportingSamplesXlsx(false);
    }
  };
  const [sampleForm] = Form.useForm();
  const [editingSample, setEditingSample] = useState<any>(null);
  const [isSavingSample, setIsSavingSample] = useState(false);
  // Inline lab_code editor state — used by the "Código Laboratorio"
  // column in the samples tab. The id here is the v2 Sample UUID
  // (legacy rows are read-only, so they don't participate).
  const [editingLabCodeId, setEditingLabCodeId] = useState<string | null>(null);
  const [labCodeDraft, setLabCodeDraft] = useState<string>("");

  // Filtros de la tabla de muestras — locales al detalle de auditoría.
  // `sampleSearchText` filtra por código de laboratorio, sample_reference,
  // waypoint o nombre de parcela. `sampleResultFilter` acepta cualquiera
  // de los valores state_name/consensus_state/state para cubrir
  // muestras v2 (consensus_state) y legacy (state_name).
  const [sampleSearchText, setSampleSearchText] = useState<string>("");
  const [sampleResultFilter, setSampleResultFilter] = useState<string | undefined>(
    undefined,
  );
  const [savingLabCode, setSavingLabCode] = useState(false);
  // Estado del modal de edición LEGACY (AuditLaboratorySample). Sólo
  // admin — hace PATCH sobre `/api/legacy-samples/{id}/`.
  const [legacyEditVisible, setLegacyEditVisible] = useState(false);
  const [legacyEditingSample, setLegacyEditingSample] = useState<any>(null);
  const [legacyForm] = Form.useForm();
  const [savingLegacy, setSavingLegacy] = useState(false);
  // Muestra sobre la que está corriendo el re-lookup del waypoint —
  // sirve para pintar spinner en el botón concreto de la fila.
  const [relookupSampleId, setRelookupSampleId] = useState<
    string | number | null
  >(null);
  const [controlPoints, setControlPoints] = useState<any[]>([]);
  const [loadingControlPoints, setLoadingControlPoints] = useState(false);
  const [isControlPoint, setIsControlPoint] = useState(false);

  // Active tab key for the detail tabs (Parcelas / PC / etc.).
  // Default lands on Parcelas — the most common entry point now that
  // history and contacts have their own dedicated sections above.
  // Segmento top-level: Resumen (ficha + mapa + KPIs + nav rápida)
  // vs Detalle (sub-tabs Parcelas, CPs, etc.). Default = Resumen
  // porque es la vista más informativa cuando aterrizas en un audit.
  const [activeSegment, setActiveSegment] =
    useState<"resumen" | "datos">("resumen");
  const [activeTab, setActiveTab] = useState<string>("parcelas");

  // Quick-create incidence modal — opened from the header Acciones
  // dropdown. Replaces the previous full-page navigation.
  const [incidenceModalOpen, setIncidenceModalOpen] = useState(false);
  // Admin-only modal for cancelling the audit. Reuses the existing
  // coordinator modal — backend permits both user types since the
  // policy was relaxed for admin in `AuditViewSet.cancel`.
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  // Bumping this triggers AuditIncidencesSection to refetch — used
  // after a successful create from the modal.
  const [incidencesRefreshKey, setIncidencesRefreshKey] = useState(0);
  // Same pattern for the unified photo gallery — bump after a
  // successful upload so the new photos show without a manual reload.
  const [photosRefreshKey, setPhotosRefreshKey] = useState(0);
  // Bumping this forces the embedded AuditMapCard to re-fetch its
  // GeoJSON. We bump it after every delete (plot / sample / control
  // point / adjacent crop) so the map drops the removed feature
  // without forcing a full page reload.
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const bumpMap = () => setMapRefreshKey((k) => k + 1);
  // Bumping this triggers FarmHistoryCard to refetch — used after a
  // successful finalize so the current audit's row in the farm
  // timeline picks up its new state ("Finalizada").
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const bumpHistory = () => setHistoryRefreshKey((k) => k + 1);
  // Panel "Cierres del auditor" — se refresca tras submit-for-review,
  // reopen, return-to-auditor y finalize para que el admin (o el
  // auditor mirando el histórico) vea el estado actualizado.
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);
  const bumpSubmissions = () => setSubmissionsRefreshKey((k) => k + 1);

  // Counts reported by each tab section to drive the tab badges.
  // Defaults to null so the badge stays hidden until the section
  // reports its first load (avoids flickering a "0" tag prematurely).
  const [cpCount, setCpCount] = useState<number | null>(null);
  const [acCount, setAcCount] = useState<number | null>(null);
  const [incidencesCount, setIncidencesCount] = useState<number | null>(null);
  const [photosCount, setPhotosCount] = useState<number | null>(null);

  // Refs + state for dynamically matching the map height to the left
  // column height (basic info + production). The right column shows
  // Contacts above the map; the map fills the remaining space so the
  // two columns end up the same total height.
  const leftColRef = useRef<HTMLDivElement | null>(null);
  const contactsRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<AuditMapCardHandle | null>(null);
  const [mapHeightPx, setMapHeightPx] = useState<number>(480);
  // Stage-aware progress for the closure-report PDF generation.
  // `null` = idle, otherwise it advances through the steps. The
  // modal renders nothing while idle and the button is `loading` for
  // any non-idle value.
  const [reportStage, setReportStage] = useState<
    null | "capturing" | "fetching" | "building" | "downloading" | "finalizing"
  >(null);
  const generatingReport = reportStage !== null;
  // Derived "audit is closed" flag — disables the Finalizar button
  // (and surfaces a confirm-disabled state) once the backend marks
  // status_of_audit='finalized'. Also covers the legacy state name
  // "Finalizada" so older deployments keep working.
  const isFinalized =
    (audit as any)?.status_of_audit === "finalized" ||
    /finaliz/i.test(audit?.audit_state?.name ?? "");
  // Nuevo estado intermedio: el auditor cerró la auditoría con
  // "Cerrar auditoría" (submit-for-review) y está pendiente de
  // revisión por el admin. En este estado la auditoría es read-only
  // para el auditor salvo que él mismo pulse "Reabrir para editar".
  // El admin puede finalizar o devolver con motivo.
  const isSubmitted = (audit as any)?.status_of_audit === "submitted";
  // Gate global de edición: `finalized` es terminal para todos y
  // `submitted` bloquea temporalmente la edición (mientras el admin
  // revisa; el auditor tiene "Reabrir" para salir). Todos los
  // controles que antes miraban `isFinalized` para inhabilitarse
  // ahora usan `isReadOnly`.
  const isReadOnly = isFinalized || isSubmitted;
  // El backend permite al admin (user_type 1/2/3) mutar muestras
  // aunque la auditoría esté finalized o submitted — el guard
  // `audit_is_locked` deja pasar a esos roles. Este flag desbloquea
  // en el frontend los botones de editar/eliminar de la tabla de
  // muestras y el CTA de "Añadir muestra" para el mismo perfil.
  const isSamplesReadOnly = isReadOnly && !isAdmin;
  // Mismo criterio para puntos de control y colindantes: cuando una
  // auditoría está `submitted` pendiente de revisión, el admin suele
  // ser el que tiene que dar de baja CPs o revisar colindantes antes
  // de finalizar. Antes se les aplicaba `isReadOnly` a secas y los
  // botones de "Dar de baja" / editar se quedaban deshabilitados;
  // el backend (close endpoint) no valida el lock, así que la UI
  // era la única barrera.
  const isCpReadOnly = isReadOnly && !isAdmin;
  const isAdjacentCropReadOnly = isReadOnly && !isAdmin;
  // Whether an admin can cancel this audit. Backend permits cancel
  // unless it's already canceled or finalized; here we additionally
  // gate on the visible state name being "Planificada" so the
  // option only shows up when it's meaningful — once the auditor
  // has done work on the audit, asking for cancellation should be
  // routed through the existing coordinator flow.
  const cancelStatus = (audit as any)?.status_of_audit;
  const canCancelAudit =
    !!audit?.id &&
    cancelStatus !== "canceled" &&
    cancelStatus !== "finalized" &&
    /planific|pendiente|planned/i.test(audit?.audit_state?.name ?? "");
  // Match the backend `is_complete_audit` rule (audit_type.name
  // contains "complet"). Aforo audits get their AuditPlots
  // pre-loaded from PlotReal at audit-creation time — the auditor
  // edits aforo, doesn't create plots from scratch.
  const isCompleteAudit = /complet/i.test(audit?.audit_type?.name ?? "");
  const isAforoAudit = !!audit?.audit_type?.name && !isCompleteAudit;

  useEffect(() => {
    if (!leftColRef.current) return;
    const ROW_GAP = 24;
    const compute = () => {
      const leftH = leftColRef.current?.getBoundingClientRect().height ?? 0;
      const contactsH = contactsRef.current?.getBoundingClientRect().height ?? 0;
      // Subtract contacts card height + the gap between contacts and map.
      const next = Math.max(320, Math.round(leftH - contactsH - ROW_GAP));
      setMapHeightPx(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(leftColRef.current);
    if (contactsRef.current) ro.observe(contactsRef.current);
    return () => ro.disconnect();
  }, [audit?.id]);

  // Format number with locale-specific thousands separator
  const formatNumber = (value: number, decimals: number = 2): string => {
    const locale = i18n.language === 'es' ? 'es-ES' : i18n.language;
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  // Map an audit state name to a semantic antd Tag color. Substring
  // match keeps it robust against minor wording differences across
  // languages/years.
  // Tag colour for the audit_state pill under the title.
  //
  // Only the three "active" states get a distinctive colour:
  //   • Planificada → orange
  //   • Finalizada  → green
  //   • Cancelada   → red
  // Legacy values ("Realizada con/sin Informe", "Checkeada", "No
  // Realizada") fall through to the neutral grey so they read as
  // historical-only.
  const getAuditStateColor = (name: string): string => {
    const n = (name || "").toLowerCase();
    if (n.includes("cancel")) return "red";
    if (n.includes("finaliz")) return "green";
    if (n.includes("planific")) return "orange";
    return "default";
  };

  // Inject styles for sample row colors
  useEffect(() => {
    const styleId = 'sample-row-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = sampleRowStyles;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // Load control points when modal opens
  useEffect(() => {
    const loadControlPoints = async () => {
      if (sampleModalVisible && audit?.id) {
        setLoadingControlPoints(true);
        try {
          const { apiClient } = await import("../../services/api/apiClient");
          const response = await apiClient.get(`/audit-control-points/list-for-audit/?audit_id=${audit.id}`);
          const points = response.data || [];
          setControlPoints(points);
          
          if (points.length === 0) {
            message.warning('No hay puntos de control registrados en esta auditoría');
          }
        } catch (error) {
          message.error('Error al cargar los puntos de control');
        } finally {
          setLoadingControlPoints(false);
        }
      }
    };

    loadControlPoints();
  }, [sampleModalVisible, audit?.id]);

  // Load audit plots when audit data is available
  useEffect(() => {
    if (audit?.plots_visited) {
      loadAuditPlots();
    }
  }, [audit?.plots_visited]);

  const loadAuditPlots = async () => {
    if (!audit?.plots_visited) return;
    
    setLoadingPlots(true);
    
    // Use plots_visited data directly and map to AuditPlot format
    const mappedPlots: AuditPlot[] = audit.plots_visited.map((plot) => ({
      id: plot.id.toString(),
      name: plot.name,
      plot_type: 0,
      plot_type_name: '',
      pattern: plot.pattern,
      pattern_name: plot.pattern_name,
      surface: plot.surface,
      plantation_date: null,
      plantation_frame_row: plot.plantation_frame_row,
      plantation_frame_column: plot.plantation_frame_column,
      plants: plot.plants,
      grafting: null,
      intermediate_wood: null,
      intermediate_wood_name: null,
      doubled_with: null,
      is_ilegal: false,
      kg_plot: plot.kg_plot || 0,
      geom: null,
      audit: audit.id,
      photo_count: 0,
    }));
    
    setAuditPlots(mappedPlots);
    setLoadingPlots(false);
    
    // Fetch full plot data with photo counts from audit-plots endpoint
    if (audit?.id) {
      try {
        const response = await auditPlotService.getPlotsByAudit(audit.id);
        
        // Handle GeoJSON FeatureCollection format
        let fullPlots: any[];
        
        // Check if response has paginated structure with results
        const geoJsonData = (response as any).results || response;
        
        if (geoJsonData && typeof geoJsonData === 'object' && 'features' in geoJsonData) {
          // GeoJSON FeatureCollection - extract properties from features
          fullPlots = geoJsonData.features.map((feature: any) => ({
            id: feature.id || feature.properties.id,
            ...feature.properties,
            geom: feature.geometry,
          }));
        } else if (Array.isArray(geoJsonData)) {
          // Direct array
          fullPlots = geoJsonData;
        } else {
          // Unknown format, keep mapped plots
          return;
        }
        
        setAuditPlots(fullPlots);
      } catch (error: any) {
        // Error handled silently
      }
    }
  };

  // Get previous location for back navigation
  const fromFarmId = (location.state as any)?.from === "farm" ? (location.state as any)?.farmId : null;

  // Build the closure-report PDF (capture maps, fetch CP/AC, render
  // to a Blob). Returns the Blob + verification code so the caller
  // can either upload it to /finalize/ or trigger a local download.
  // No download is triggered here — that responsibility lives with
  // the caller.
  const generateClosureReport = async (): Promise<
    { verificationCode: string; blob: Blob } | null
  > => {
    if (!audit?.id) return null;
    try {
      setReportStage("capturing");
      let mapCapture: AuditReportData["map_capture"];
      let samplesMapCapture: AuditReportData["samples_map_capture"];
      try {
        mapCapture =
          (await mapRef.current?.captureForReport("main")) ?? undefined;
      } catch (e) {
        console.warn(
          "[audit-report] main map capture failed; continuing without map snapshot",
          e,
        );
      }
      // Second map: only when the audit has samples. Captured
      // sequentially (not in parallel) because both share the same
      // Leaflet instance and would race against each other on
      // hide/restore of layers.
      const hasSamples = (audit.samples_summary?.length ?? 0) > 0;
      if (hasSamples) {
        try {
          samplesMapCapture =
            (await mapRef.current?.captureForReport("samples")) ?? undefined;
        } catch (e) {
          console.warn(
            "[audit-report] samples map capture failed; continuing without samples snapshot",
            e,
          );
        }
      }

      setReportStage("fetching");
      const [cpResult, acResult] = await Promise.all([
        audit.farm?.id
          ? controlPointService
              .getOpenControlPointsForFarm(audit.farm.id, audit.id)
              .catch((e) => {
                console.warn("[audit-report] CP fetch failed", e);
                return { control_points: [] as any[] };
              })
          : Promise.resolve({ control_points: [] as any[] }),
        audit.farm?.id
          ? adjacentCropService
              .getOpenAdjacentCropsForFarm(audit.farm.id, audit.id)
              .catch((e) => {
                console.warn("[audit-report] AC fetch failed", e);
                return { adjacent_crops: [] as any };
              })
          : Promise.resolve({ adjacent_crops: [] as any }),
      ]);

      setReportStage("building");
      const data = buildAuditReportData({
        audit,
        plots: auditPlots,
        controlPoints: extractCollection(cpResult?.control_points),
        adjacentCrops: extractCollection(acResult?.adjacent_crops),
        mapCapture,
        samplesMapCapture,
      });
      const { buildAuditReportPdf } = await import(
        "../../utils/auditReportPdf"
      );
      const { blob, verificationCode } = await buildAuditReportPdf(data);
      return { verificationCode, blob };
    } catch (err) {
      console.error("[audit-report] failed", err);
      appMessage.error(
        t("audit.report_error", "No se pudo generar el acta de cierre"),
      );
      return null;
    }
  };

  // Trigger a local download of an in-memory Blob (the just-built
  // closure report) without going through the backend. Used after
  // a successful finalize so the auditor gets the PDF immediately.
  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 1000);
  };

  // Confirm-and-finalise flow. Builds the closure report locally,
  // calls the backend `/audits/{id}/finalize/` action with the
  // verification code printed on the PDF, then refetches the audit
  // so the state tag and "isFinalized" gates update without a page
  // reload.
  // Build the list of pre-requisites a user must satisfy *before*
  // finalising an audit. Returns the labels of every missing item,
  // or an empty array when the audit is ready to be closed. The
  // backend will run the same validation defensively, but checking
  // here lets us surface the exact missing fields without paying
  // an HTTP round-trip.
  const collectFinalizeBlockers = (): string[] => {
    if (!audit) return [t("audit.finalize_block_loading", "Cargando auditoría…")];
    const missing: string[] = [];
    // Nota: `has_audit_act` ya no bloquea el finalize. Ahora el
    // sistema genera el acta como parte del closure y, si el
    // auditor no subió su propia acta, se copia el PDF generado
    // en `audit_act_file` (ver `_perform_finalize` en el backend).
    if (!audit.start_date) {
      missing.push(t("audit.finalize_block_start_date", "Fecha de inicio"));
    }
    if (!audit.start_time) {
      missing.push(t("audit.finalize_block_start_time", "Hora de inicio"));
    }
    if (!audit.finishing_time) {
      missing.push(t("audit.finalize_block_finishing_time", "Hora de finalización"));
    }
    const hasContact =
      !!(audit as any).contact_receiver_unified ||
      ((audit as any).contact_receiver || "").trim().length > 0;
    if (!hasContact) {
      missing.push(t("audit.finalize_block_contact", "Contacto que nos recibe"));
    }
    if (!audit.is_under_net) {
      missing.push(t("audit.finalize_block_is_under_net", "Finca mallada"));
    }
    if (!audit.production_type) {
      missing.push(t("audit.finalize_block_production_type", "Tipo de producción"));
    }

    // Aforo-only check: every precharged AuditPlot must have been
    // saved through the review wizard at least once. We rely on
    // `reviewed_at` — kg/blocks aren't reliable because a legit
    // review can confirm zero production. The backend mirrors this
    // and also checks control points / adjacent crops, surfacing
    // their missing items via the 400 response.
    if (isAforoAudit) {
      const pendingPlots = auditPlots
        .filter((p) => !(p as any).reviewed_at)
        .map((p) => (p as any).name || `#${(p as any).id}`);
      if (pendingPlots.length > 0) {
        const preview = pendingPlots.slice(0, 5).join(", ");
        const more =
          pendingPlots.length > 5 ? ` (+${pendingPlots.length - 5})` : "";
        missing.push(
          t(
            "audit.finalize_block_aforo_pending",
            "Revisión pendiente en parcelas",
          ) + `: ${preview}${more}`,
        );
      }
    }
    return missing;
  };

  // `adminShortcut=true` sirve al atajo "Cerrar y finalizar (admin)":
  // salta el paso intermedio del auditor y llama a
  // /audits/{id}/admin-close-and-finalize/. La lógica de bloqueadores,
  // generación del PDF y respuesta es idéntica al flujo estándar.
  // --- Reapertura de auditoría (admin) ---
  // Botón visible cuando isAdmin && isFinalized. Abre un modal con
  // motivo opcional y warning si `aforo_synced_at` está populado
  // (habrá que reenviarlo tras cerrar de nuevo). Al confirmar POSTea
  // a /api/v1/audits/{id}/reopen/ y refetch.
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);

  const openReopenModal = () => {
    setReopenReason("");
    setReopenModalOpen(true);
  };

  const handleReopenAudit = async () => {
    if (!audit?.id) return;
    setReopening(true);
    try {
      await apiClient.post(`/v1/audits/${audit.id}/reopen/`, {
        reason: reopenReason.trim(),
      });
      message.success(
        t(
          "audit.reopen_success",
          "Auditoría reabierta — ya puedes editarla y volver a cerrarla",
        ),
      );
      setReopenModalOpen(false);
      refetch();
    } catch (err: any) {
      const resp =
        err?.originalError?.response?.data ||
        err?.response?.data ||
        err?.data ||
        {};
      const detail =
        resp?.detail ||
        err?.message ||
        t("audit.reopen_error", "No se pudo reabrir la auditoría");
      message.error(detail);
    } finally {
      setReopening(false);
    }
  };

  const handleFinalizeAudit = (adminShortcut: boolean = false) => {
    if (!audit?.id || isFinalized) return;
    // Block early when prerequisites are missing — the auditor sees
    // a checklist of what's left to fill instead of a generic 400
    // from the backend.
    const blockers = collectFinalizeBlockers();
    if (blockers.length > 0) {
      // Atajo cuando lo único pendiente son parcelas arrancadas: en
      // vez de pedirle al auditor que entre fila por fila al wizard
      // de revisión, le ofrecemos cerrarlas todas con un motivo común.
      const aforoPending = isAforoAudit
        ? auditPlots.filter((p) => !(p as any).reviewed_at)
        : [];
      const hasAforoCta = aforoPending.length > 0;
      // Usamos modal.confirm (en vez de modal.warning) para que el
      // pie del modal exponga siempre Cancelar como botón propio,
      // además del CTA de baja en bloque cuando aplica.
      modal.confirm({
        title: t(
          "audit.finalize_blocked_title",
          "No se puede finalizar todavía",
        ),
        icon: <WarningOutlined style={{ color: "#faad14" }} />,
        width: 520,
        okText: hasAforoCta
          ? t(
              "audit.finalize_bulk_close",
              "Dar de baja las {{n}} parcelas pendientes como arrancadas",
              { n: aforoPending.length },
            )
          : t("common.ok", "Entendido"),
        okButtonProps: hasAforoCta
          ? { danger: true, icon: <CloseCircleOutlined /> }
          : { style: { display: "none" } },
        cancelText: t("common.cancel", "Cancelar"),
        onOk: hasAforoCta
          ? () => {
              setBulkClosePlots(aforoPending);
              setBulkCloseReason("");
              setBulkCloseModalOpen(true);
            }
          : undefined,
        content: (
          <div>
            <Typography.Paragraph style={{ marginBottom: 8 }}>
              {t(
                "audit.finalize_blocked_body",
                "Antes de cerrar la auditoría hay que completar los siguientes apartados:",
              )}
            </Typography.Paragraph>
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              {blockers.map((label) => (
                <li key={label} style={{ marginBottom: 4 }}>
                  <Typography.Text>{label}</Typography.Text>
                </li>
              ))}
            </ul>
            {hasAforoCta && (
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}
              >
                {t(
                  "audit.finalize_aforo_hint",
                  "Si esas parcelas están arrancadas, puedes darlas de baja todas a la vez con un motivo común usando el botón de la derecha.",
                )}
              </Typography.Paragraph>
            )}
          </div>
        ),
      });
      return;
    }
    modal.confirm({
      title: adminShortcut
        ? t(
            "audit.admin_close_confirm_title",
            "Cerrar y finalizar (admin)",
          )
        : t("audit.finalize_confirm_title", "Finalizar auditoría"),
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            {adminShortcut
              ? t(
                  "audit.admin_close_confirm_body",
                  "Se saltará el paso de cierre por parte del auditor. Se guardará el snapshot inmutable con tu usuario como responsable del cierre, se generará el acta de cierre y la auditoría pasará a estado Finalizada. Se ejecutará el traspaso a parcelas canónicas, nombre de finca, revisiones, etc.",
                )
              : t(
                  "audit.finalize_confirm_body",
                  "Se generará el acta de cierre y la auditoría pasará a estado Finalizada.",
                )}
          </Typography.Paragraph>
          <Typography.Paragraph type="warning" style={{ marginBottom: 0 }}>
            {t(
              "audit.finalize_confirm_warning",
              "Esta acción no se puede deshacer desde la propia auditoría.",
            )}
          </Typography.Paragraph>
        </div>
      ),
      okText: adminShortcut
        ? t("audit.admin_close_confirm_ok", "Cerrar y finalizar")
        : t("audit.finalize_confirm_ok", "Finalizar y generar acta"),
      okButtonProps: { type: "primary", icon: <CheckCircleOutlined /> },
      cancelText: t("common.cancel", "Cancelar"),
      width: 480,
      onOk: async () => {
        const result = await generateClosureReport();
        if (!result) {
          setReportStage(null);
          return;
        }
        // La subida al backend (POST /finalize/) puede reintentarse
        // con `force_farm_name_mismatch=true` si el servidor devuelve
        // 409 tras detectar divergencia entre `audit.farm_name` y el
        // nombre real actual de la finca vinculada. Extraemos aquí
        // el POST para poder llamarlo de nuevo desde el modal de
        // confirmación sin regenerar el PDF ni el verification code.
        const fileName =
          `acta-cierre-${(audit.reference || `auditoria-${audit.id}`).replace(/\s+/g, "_")}.pdf`;
        const reportFile = new File([result.blob], fileName, {
          type: "application/pdf",
        });
        const finalizeUrl = adminShortcut
          ? `/audits/${audit.id}/admin-close-and-finalize/`
          : `/audits/${audit.id}/finalize/`;

        const postFinalize = async (force: boolean) => {
          const form = new FormData();
          form.append("verification_code", result.verificationCode);
          form.append("closure_report_file", reportFile);
          if (force) {
            form.append("force_farm_name_mismatch", "true");
          }
          await apiClient.post(finalizeUrl, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        };

        const onFinalizeSuccess = async () => {
          // Al cerrar, el backend regenera el acta SGS en `audit_act_file`
          // vía `generate_or_regenerate_acta` dentro de `_perform_finalize`.
          // Descargamos ESE PDF (el nuevo, con firmas + mapa + bloque de
          // parcelas) en lugar del blob local — el blob local es la
          // plantilla antigua de "acta de cierre" (frontend-rendered)
          // que se envía al backend sólo para almacenar `closure_report_file`
          // como registro inmutable del momento del cierre. Si la descarga
          // del acta SGS fallara, caemos al blob local para que el usuario
          // al menos se lleve una copia.
          const sgsFileName =
            `acta-${(audit.reference || `auditoria-${audit.id}`).replace(/\s+/g, "_")}.pdf`;
          try {
            const resp = await apiClient.get(
              `/audits/${audit.id}/download-audit-act/`,
              { responseType: "blob" },
            );
            const sgsBlob = new Blob([resp.data], { type: "application/pdf" });
            triggerBlobDownload(sgsBlob, sgsFileName);
          } catch (e) {
            // Backend aún no ha propagado el acta — fallback al local.
            triggerBlobDownload(result.blob, fileName);
          }
          appMessage.success(
            t(
              "audit.finalize_success",
              "Auditoría finalizada. Acta descargada con código {{code}}",
              { code: result.verificationCode },
            ),
          );
          refetch();
          // Force the farm-history card to re-fetch so the row that
          // represents this audit flips from its previous state to
          // "Finalizada" without needing a page reload.
          bumpHistory();
          bumpSubmissions();
        };

        try {
          setReportStage("finalizing");
          await postFinalize(false);
          onFinalizeSuccess();
        } catch (err: any) {
          console.error("[audit-report] finalize POST failed", err);
          // The backend mirrors `collectFinalizeBlockers` and can
          // reject with HTTP 400 + `missing_fields[]`. Surface that
          // list with the same checklist UI used for local pre-
          // checks so the auditor sees exactly what's pending —
          // including the aforo-only items the frontend can't fully
          // verify (CP / adjacent-crop reviews).
          //
          // El interceptor de apiClient transforma el error 4xx en un
          // objeto plano sin `response`/`data`; el original queda en
          // `originalError`. Sin este fallback el modal de checklist
          // no se mostraba nunca y caía a un toast con solo el detail
          // ("No se puede finalizar todavía: faltan los siguientes
          // apartados.") sin la lista — confuso para el auditor.
          const errorData =
            err?.response?.data ?? err?.originalError?.response?.data;
          const backendMissing: string[] | undefined =
            errorData?.missing_fields;
          const detail =
            errorData?.detail ||
            err?.message ||
            t(
              "audit.finalize_error",
              "El acta se generó pero no se pudo marcar como finalizada en el servidor",
            );
          // 409 con `code=farm_name_mismatch`: el snapshot de nombre
          // capturado en la audit ya no coincide con el nombre real
          // actual de la finca vinculada. Pedimos confirmación al
          // admin antes de reintentar con force=true.
          if (errorData?.code === "farm_name_mismatch") {
            const auditFarmName = errorData?.audit_farm_name || "";
            const realFarmName = errorData?.real_farm_name || "";
            modal.confirm({
              title: t(
                "audit.finalize_farm_name_mismatch_title",
                "El nombre de finca ha cambiado",
              ),
              icon: <WarningOutlined style={{ color: "#faad14" }} />,
              width: 560,
              okText: t(
                "audit.finalize_farm_name_mismatch_ok",
                "Continuar y finalizar",
              ),
              okButtonProps: { type: "primary", danger: true },
              cancelText: t("common.cancel", "Cancelar"),
              content: (
                <div>
                  <Typography.Paragraph style={{ marginBottom: 8 }}>
                    {t(
                      "audit.finalize_farm_name_mismatch_body",
                      "El nombre de finca capturado en la auditoría no coincide con el nombre real actual de la finca vinculada. Revísalo antes de continuar.",
                    )}
                  </Typography.Paragraph>
                  <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
                    <li>
                      <Typography.Text strong>
                        {t(
                          "audit.finalize_farm_name_mismatch_audit",
                          "En la auditoría",
                        )}
                        :
                      </Typography.Text>{" "}
                      <Typography.Text>{auditFarmName || "—"}</Typography.Text>
                    </li>
                    <li>
                      <Typography.Text strong>
                        {t(
                          "audit.finalize_farm_name_mismatch_real",
                          "Nombre real actual",
                        )}
                        :
                      </Typography.Text>{" "}
                      <Typography.Text>{realFarmName || "—"}</Typography.Text>
                    </li>
                  </ul>
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {t(
                      "audit.finalize_farm_name_mismatch_hint",
                      "Si es un cambio de nombre legítimo, puedes continuar. Si la auditoría apunta a la finca equivocada, cancela y corrígelo antes.",
                    )}
                  </Typography.Paragraph>
                </div>
              ),
              onOk: async () => {
                try {
                  setReportStage("finalizing");
                  await postFinalize(true);
                  onFinalizeSuccess();
                } catch (err2: any) {
                  const detail2 =
                    err2?.response?.data?.detail ||
                    err2?.originalError?.response?.data?.detail ||
                    err2?.message ||
                    t(
                      "audit.finalize_error",
                      "El acta se generó pero no se pudo marcar como finalizada en el servidor",
                    );
                  appMessage.error(detail2);
                } finally {
                  setReportStage(null);
                }
              },
              onCancel: () => {
                setReportStage(null);
              },
            });
            // No limpies el stage en el finally externo: el flujo
            // continúa dentro del modal.confirm anidado.
            return;
          }
          if (
            Array.isArray(backendMissing) &&
            backendMissing.length > 0
          ) {
            modal.warning({
              title: t(
                "audit.finalize_blocked_title",
                "No se puede finalizar todavía",
              ),
              icon: <WarningOutlined style={{ color: "#faad14" }} />,
              width: 560,
              okText: t("common.ok", "Entendido"),
              content: (
                <div>
                  <Typography.Paragraph style={{ marginBottom: 8 }}>
                    {detail}
                  </Typography.Paragraph>
                  <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    {backendMissing.map((label) => (
                      <li key={label} style={{ marginBottom: 4 }}>
                        <Typography.Text>{label}</Typography.Text>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            });
          } else {
            appMessage.error(detail);
          }
        } finally {
          setReportStage(null);
        }
      },
    });
  };

  // ── Nuevo paso intermedio: cierre por el auditor ──────────────
  // El auditor pulsa "Cerrar auditoría" → POST /audits/{id}/submit-
  // for-review/. El backend congela un JSON con todo el estado y
  // deja el status en `submitted`. NO materializa nada — eso lo
  // hace el admin con "Finalizar".
  const handleSubmitForReview = () => {
    if (!audit?.id || isSubmitted || isFinalized) return;
    modal.confirm({
      title: t("audit.submit_confirm_title", "Cerrar auditoría"),
      icon: <CheckCircleOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            {t(
              "audit.submit_confirm_body",
              "Se guardará una copia inmutable de la información tal como está ahora — sirve como prueba de lo que has entregado. El administrador la revisará y podrá finalizarla o devolvértela con motivo.",
            )}
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t(
              "audit.submit_confirm_hint",
              "Si necesitas retocar algo después, puedes pulsar 'Reabrir para editar' en esta misma pantalla.",
            )}
          </Typography.Paragraph>
        </div>
      ),
      okText: t("audit.submit_confirm_ok", "Cerrar y enviar a revisión"),
      okButtonProps: { type: "primary" },
      cancelText: t("common.cancel", "Cancelar"),
      width: 520,
      onOk: async () => {
        try {
          await apiClient.post(`/audits/${audit.id}/submit-for-review/`);
          appMessage.success(
            t(
              "audit.submit_success",
              "Auditoría cerrada. Pendiente de revisión por el administrador.",
            ),
          );
          await refetch();
          bumpHistory();
          bumpSubmissions();
        } catch (err: any) {
          const detail =
            err?.response?.data?.detail ??
            err?.message ??
            String(err);
          appMessage.error(detail);
        }
      },
    });
  };

  // Auditor reabre su propia auditoría cerrada para poder editar.
  // Las submissions anteriores se conservan como histórico visible
  // en la pestaña de auditoría; sirve de prueba de lo que había
  // entregado inicialmente.
  const handleReopenSubmission = () => {
    if (!audit?.id || !isSubmitted) return;
    modal.confirm({
      title: t("audit.reopen_confirm_title", "Reabrir para editar"),
      icon: <WarningOutlined style={{ color: "#faad14" }} />,
      content: (
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {t(
            "audit.reopen_confirm_body",
            "La auditoría volverá a estar editable. La copia inmutable del cierre anterior se conserva en el histórico. Cuando termines, tendrás que volver a pulsar 'Cerrar auditoría'.",
          )}
        </Typography.Paragraph>
      ),
      okText: t("audit.reopen_confirm_ok", "Reabrir"),
      cancelText: t("common.cancel", "Cancelar"),
      onOk: async () => {
        try {
          await apiClient.post(`/audits/${audit.id}/reopen-submission/`);
          appMessage.success(
            t("audit.reopen_success", "Auditoría reabierta."),
          );
          await refetch();
          bumpSubmissions();
        } catch (err: any) {
          const detail =
            err?.response?.data?.detail ?? err?.message ?? String(err);
          appMessage.error(detail);
        }
      },
    });
  };

  // Admin devuelve la submission al auditor con motivo obligatorio.
  // El motivo se guarda en la submission y baja el status a
  // `performed`; el auditor lo verá al abrir la auditoría.
  const handleReturnToAuditor = () => {
    if (!audit?.id || !isSubmitted) return;
    let reasonRef = "";
    modal.confirm({
      title: t("audit.return_confirm_title", "Devolver al auditor"),
      icon: <WarningOutlined style={{ color: "#faad14" }} />,
      width: 520,
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            {t(
              "audit.return_confirm_body",
              "Escribe el motivo por el que devuelves la auditoría. El auditor lo verá y podrá corregir antes de volver a cerrarla.",
            )}
          </Typography.Paragraph>
          <Input.TextArea
            rows={4}
            maxLength={800}
            showCount
            placeholder={t(
              "audit.return_reason_placeholder",
              "Ej: falta la foto del control point 3, revisar la variedad de la parcela P1.",
            )}
            onChange={(e) => {
              reasonRef = e.target.value;
            }}
          />
        </div>
      ),
      okText: t("audit.return_confirm_ok", "Devolver con motivo"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel", "Cancelar"),
      onOk: async () => {
        const reason = reasonRef.trim();
        if (!reason) {
          appMessage.error(
            t("audit.return_reason_required", "El motivo es obligatorio."),
          );
          throw new Error("reason required");
        }
        try {
          await apiClient.post(`/audits/${audit.id}/return-to-auditor/`, {
            reason,
          });
          appMessage.success(
            t("audit.return_success", "Auditoría devuelta al auditor."),
          );
          await refetch();
          bumpSubmissions();
        } catch (err: any) {
          const detail =
            err?.response?.data?.detail ?? err?.message ?? String(err);
          appMessage.error(detail);
          throw err;
        }
      },
    });
  };

  // Open the stored closure-report PDF in a new tab. Mirrors the
  // `handleViewAuditAct` flow: pull through the authenticated proxy
  // (bucket objects are private in prod) and use a blob URL so the
  // browser opens it inline.
  const handleDownloadClosureReport = async () => {
    if (!audit?.id || !(audit as any).has_closure_report) return;
    const hide = appMessage.loading(
      t("audit.closure_opening", "Abriendo acta de cierre…"),
      0,
    );
    try {
      const response = await apiClient.get(
        `/audits/${audit.id}/download-closure-report/`,
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      const detail =
        e?.response?.status === 404
          ? t(
              "audit.closure_missing",
              "El acta de cierre no se encuentra en el almacenamiento.",
            )
          : t(
              "audit.closure_open_error",
              "No se pudo abrir el acta de cierre.",
            );
      appMessage.error(detail);
    } finally {
      hide();
    }
  };

  const handleViewMap = () => {
    if (audit?.id) {
      navigate(`/audits/${audit.id}/map`, {
        state: { from: location.pathname },
      });
    }
  };

  const handleViewProducerContacts = () => {
    if (audit?.id) {
      navigate(`/audits/${audit.id}/producer/contacts`, {
        state: { from: location.pathname },
      });
    }
  };

  const handleViewAuditAct = async () => {
    if (!audit?.id || !audit.has_audit_act) return;
    // The acta endpoint is authenticated, so we can't just
    // `window.open(...)` — a fresh browser tab doesn't carry the
    // axios Authorization header and the proxy returns 401. Fetch
    // the PDF through the authenticated client, wrap it in a blob
    // URL, and open that. Works the same way in dev (local file
    // streamed by Django) and prod (private S3 streamed through
    // Django using the server's IAM creds).
    const hide = message.loading(
      t("audit.opening_act", "Abriendo acta…"),
      0,
    );
    try {
      const response = await apiClient.get(
        `/audits/${audit.id}/download-audit-act/`,
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const win = window.open(objectUrl, "_blank");
      if (!win) {
        // Popup blocker — fall back to a download link click.
        const a = document.createElement("a");
        a.href = objectUrl;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      }
      // Revoke the blob URL after the tab has had time to load it.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e: any) {
      const detail =
        e?.response?.status === 404
          ? t(
              "audit.act_missing",
              "El archivo del acta no se encuentra en el almacenamiento. Vuélvelo a subir.",
            )
          : t("audit.act_open_error", "No se pudo abrir el acta.");
      message.error(detail);
    } finally {
      hide();
    }
  };

  const handleUploadAuditAct = () => {
    setUploadModalVisible(true);
  };

  // Generar (o regenerar) el acta oficial vía
  // `POST /audits/{id}/generate-acta/`. Útil cuando el productor NO
  // firmó via móvil: admin genera el PDF (con "[Firma no disponible]"
  // como placeholder), lo descarga para imprimir y recoger firma en
  // papel. Admin puede llamarlo también para regenerar el acta tras
  // cambios en los datos de la auditoría.
  const [generatingActa, setGeneratingActa] = useState(false);

  // Inline "Enviar aforo" en el Resumen (al lado del tag). La lógica
  // completa (preview destino, source_id, etc.) vive en `AforoPushCard`
  // dentro de Datos; aquí sólo exponemos un botón rápido con el error
  // debajo. Comparte endpoint — `POST /audits/<id>/push-aforo/`.
  const [sendingAforo, setSendingAforo] = useState(false);
  const [aforoInlineError, setAforoInlineError] = useState<string | null>(null);
  const handlePushAforoInline = async () => {
    if (!audit?.id) return;
    setSendingAforo(true);
    setAforoInlineError(null);
    try {
      await apiClient.post(`/audits/${audit.id}/push-aforo/`);
      message.success(
        t(
          "audit.aforo.push_success_short",
          "Aforo enviado a Certification",
        ),
      );
      refetch();
    } catch (err: any) {
      const resp =
        err?.originalError?.response?.data ||
        err?.response?.data ||
        err?.data ||
        {};
      const detail =
        resp?.detail ||
        (typeof resp?.errors === "object"
          ? Object.entries(resp.errors)
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join(" · ")
          : null) ||
        err?.message ||
        t(
          "audit.aforo.push_error_generic",
          "No se pudo enviar el aforo a Certification.",
        );
      setAforoInlineError(detail);
      message.error(detail);
      refetch();
    } finally {
      setSendingAforo(false);
    }
  };
  const handleGenerateActa = async () => {
    if (!audit?.id) return;
    setGeneratingActa(true);
    const hide = message.loading(
      t("audit.generating_act", "Generando acta…"), 0,
    );
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      const resp = await apiClient.post(
        `/audits/${audit.id}/generate-acta/`,
      );
      hide();
      message.success(t("audit.generated_act", "Acta generada"));
      // La respuesta trae una URL firmada S3 — se puede abrir sin
      // auth. Click sincrónico sobre un `<a target="_blank">` desde
      // dentro del mismo tick que acaba de cerrar el await: la mayoría
      // de navegadores lo permiten; el `window.open` dentro de un
      // `setTimeout` del flujo anterior quedaba fuera del gesto del
      // usuario y los popup-blockers lo cortaban (síntoma de prod).
      const signedUrl: string | undefined = resp?.data?.audit_act_url;
      if (signedUrl) {
        const a = document.createElement("a");
        a.href = signedUrl;
        a.target = "_blank";
        a.rel = "noopener";
        a.download = `acta-${audit.reference ?? audit.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Refetch en background para refrescar el tag "Estado del acta"
      // y las opciones del dropdown (Ver / Regenerar).
      refetch();
    } catch (e: any) {
      hide();
      const detail =
        e?.response?.data?.detail
          ?? t("audit.act_generate_error", "No se pudo generar el acta.");
      message.error(detail);
    } finally {
      setGeneratingActa(false);
    }
  };

  const [downloadingBundle, setDownloadingBundle] = useState(false);
  // ZIP bundle = backend-rendered Excel + client-side plot-map PNG.
  // The PNG comes from `AuditMapCard.composePlotMapImage()` so the
  // image matches exactly what the user sees on the map (same
  // branding, same labels, same overlap clustering). The legacy
  // server-side PDF render is dropped on purpose — it forced the
  // backend to spin up a headless tile fetch for every export and
  // the client snapshot already covers the use case.
  const handleDownloadBundle = async () => {
    if (!audit?.id) return;
    const hide = message.loading(
      t("audit.bundle_loading", "Generando paquete (Excel + mapa)..."),
      0,
    );
    setDownloadingBundle(true);
    try {
      const [{ fetchAuditExcelBlob }, JSZipMod] = await Promise.all([
        import("../../services/api/auditBundleService"),
        import("jszip"),
      ]);

      // Kick off both artefacts in parallel — neither depends on
      // the other and the Excel render is the slow path.
      const [excelResult, mapResult] = await Promise.all([
        fetchAuditExcelBlob(audit.id, { reference: audit.reference }),
        mapRef.current?.composePlotMapImage?.() ?? Promise.resolve(null),
      ]);

      const zip = new JSZipMod.default();
      zip.file(excelResult.filename, excelResult.blob);
      if (mapResult) {
        zip.file(mapResult.fileName, mapResult.blob);
      } else {
        // We still ship the Excel; just warn the user the map step
        // failed (likely no parcelas / no farm geometry).
        message.warning(
          t(
            "audit.bundle_map_skipped",
            "No se pudo generar el mapa; el ZIP sólo incluye el Excel.",
          ),
        );
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `auditoria_${audit.reference || audit.id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      message.success(
        t("audit.bundle_success", "Paquete descargado correctamente"),
      );
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("audit.bundle_error", "No se pudo generar el paquete");
      message.error(detail);
    } finally {
      hide();
      setDownloadingBundle(false);
    }
  };

  const handleUploadAuditData = () => {
    setUploadDataModalVisible(true);
  };

  const handleUploadSuccess = () => {
    refetch();
    loadAuditPlots();
    // The Excel-upload path can land new samples (and other items)
    // for this audit in one go — refresh the farm history so it
    // doesn't drift from the actual sample count.
    bumpHistory();
  };

  const handleCreatePlot = () => {
    if (audit?.id) {
      navigate(`/audits/${audit.id}/plots/create`, {
        state: { fromPlanningId, planningName },
      });
    }
  };

  // ─── Fusión de AuditPlots ──────────────────────────────────────
  // Mirror del flujo móvil (AuditPlotsListScreen). El primer id
  // seleccionado es el "target" — conserva identidad; el resto son
  // absorbidos: sus plantings se re-linkean con `ordering += offset`
  // y los AuditPlot absorbidos se destruyen.
  const handleStartMerge = () => {
    setMergeMode(true);
    setMergeSelectedIds([]);
  };
  const handleCancelMerge = () => {
    setMergeMode(false);
    setMergeSelectedIds([]);
  };
  const handleConfirmMerge = () => {
    if (!audit?.id) return;
    if (mergeSelectedIds.length < 2) {
      appMessage.warning(
        "Selecciona al menos 2 parcelas para fusionar (la primera será la que conserve).",
      );
      return;
    }
    const [targetId, ...absorbedIds] = mergeSelectedIds;
    const target = auditPlots.find((p) => Number(p.id) === targetId);
    const absorbed = absorbedIds.map((id) =>
      auditPlots.find((p) => Number(p.id) === id),
    );
    // Ant 5: modal via useApp() para que participe del <App> context
    modal.confirm({
      title: "Fusionar parcelas",
      content: (
        <div>
          <p>
            Se conservará <strong>{target?.name ?? "—"}</strong> y absorberá:
          </p>
          <ul>
            {absorbed.map((p) => (
              <li key={p?.id}>{p?.name ?? "—"}</li>
            ))}
          </ul>
          <p>
            Las plantaciones de las absorbidas pasarán a{" "}
            <strong>{target?.name ?? "—"}</strong>. Fotos, aforo, bloques y
            comentarios de las absorbidas <strong>se descartan</strong>. Esta
            acción no se puede deshacer.
          </p>
        </div>
      ),
      okText: "Fusionar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setMergeSubmitting(true);
        try {
          await auditPlotService.mergePlots(
            Number(audit.id),
            targetId,
            absorbedIds,
          );
          appMessage.success("Parcelas fusionadas correctamente.");
          handleCancelMerge();
          await loadAuditPlots();
        } catch (err) {
          const detail =
            (err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail ?? (err as Error).message;
          appMessage.error(`Error al fusionar: ${detail}`);
        } finally {
          setMergeSubmitting(false);
        }
      },
    });
  };

  // Sincroniza los AuditPlots de esta auditoría con los PlotReal
  // actuales de la finca. Útil cuando la auditoría se creó antes de
  // que la finca tuviera canónicas materializadas (el signal de
  // precarga sólo se dispara en ese momento). El endpoint es
  // idempotente: si ya hay AuditPlots no los duplica.
  const [prechargingPlots, setPrechargingPlots] = useState(false);
  const handlePrechargePlots = async () => {
    if (!audit?.id) return;
    setPrechargingPlots(true);
    try {
      const res = await apiClient.post<{
        audit_plots_before: number;
        audit_plots_after: number;
        created: number;
        linked_by_name: number;
        farm_plot_reals: number;
      }>(`/audits/${audit.id}/precharge-plots/`);
      const r = res.data;
      if (r.created === 0 && r.linked_by_name === 0) {
        message.info(
          t(
            "audit.precharge_noop",
            "No se ha precargado ninguna parcela ({{n}} canónicas en la finca).",
            { n: r.farm_plot_reals },
          ),
        );
      } else {
        message.success(
          t(
            "audit.precharge_success",
            "Precargadas: {{c}} nuevas, {{l}} reatadas. Total parcelas: {{t}}.",
            {
              c: r.created,
              l: r.linked_by_name,
              t: r.audit_plots_after,
            },
          ),
        );
      }
      await loadAuditPlots();
      refetch();
      // Refresh the embedded map so las nuevas AuditPlots (con su
      // geometría) aparezcan dibujadas sin necesidad de recargar.
      bumpMap();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Error";
      message.error(detail);
    } finally {
      setPrechargingPlots(false);
    }
  };

  const handleEditPlot = (plot: AuditPlot | PlotVisited) => {
    if (audit?.id) {
      // Navigate to plot form page for editing
      navigate(`/audits/${audit.id}/plots/${plot.id}/edit`, {
        state: { fromPlanningId, planningName },
      });
    }
  };

  const handleDeletePlot = async (plotId: number) => {
    if (!audit?.id) return;

    try {
      await auditPlotService.deletePlot(plotId.toString(), audit.id);
      message.success(
        t("plot.delete_success") || "Parcela eliminada correctamente"
      );
      refetch();
      loadAuditPlots();
      bumpMap();
    } catch (error: any) {
      message.error(
        error.message ||
          t("plot.delete_error") ||
          "Error al eliminar la parcela"
      );
    }
  };

  const handleUploadPhotos = () => {
    setPhotoUploadModalVisible(true);
  };

  const handleViewPhotos = (plotId: string, plotName: string) => {
    setSelectedPlot({ id: plotId, name: plotName });
    setPhotoGalleryModalVisible(true);
  };

  const handlePhotoUploadSuccess = () => {
    loadAuditPlots();
    refetch();
    setPhotosRefreshKey((n) => n + 1);
  };

  const handlePhotoDeleted = () => {
    loadAuditPlots();
    refetch();
  };

  // Plots visited columns - using AuditPlot type for photo_count support
  const plotColumns = [
    {
      title: t("audit.plot_name_short", "Nombre"),
      dataIndex: "name",
      key: "name",
      width: 140,
      ellipsis: true,
      sorter: (a: AuditPlot, b: AuditPlot) => a.name.localeCompare(b.name),
      // Chip verde `D×N` cuando la parcela es doblada (2+ plantings).
      // Consistente con la app móvil (AuditPlotsListScreen). El backend
      // ya envía `is_doubled` y `plantings.length` via AuditPlotSerializer.
      render: (name: string, record: AuditPlot) => {
        const n = record.plantings?.length ?? 0;
        const doubled = record.is_doubled || n >= 2;
        return (
          <span>
            {name}
            {doubled ? (
              <Tag
                color="green"
                style={{ marginLeft: 6, fontSize: 11, padding: "0 4px" }}
                title={`Parcela doblada con ${n} plantaciones`}
              >
                D×{n}
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: t("audit.pattern", "Patrón"),
      dataIndex: "pattern_name",
      key: "pattern_name",
      width: 120,
      ellipsis: true,
      sorter: (a: AuditPlot, b: AuditPlot) => (a.pattern_name || "").localeCompare(b.pattern_name || ""),
    },
    {
      title: t("audit.surface_ha_short", "Sup. (ha)"),
      dataIndex: "surface",
      key: "surface",
      width: 95,
      render: (value: any) => formatNumber(parseFloat(value), 2),
      align: "right" as const,
      sorter: (a: AuditPlot, b: AuditPlot) => (parseFloat(a.surface as any) || 0) - (parseFloat(b.surface as any) || 0),
    },
    {
      title: t("audit.plantation_frame_short", "Marco"),
      key: "plantation_frame",
      width: 90,
      align: "center" as const,
      // Convención agrícola: `row × column` = distancia entre filas
      // (calles) × distancia entre árboles en la fila. Ej. 5,75×2.
      // Mantiene coherencia con el móvil (AuditFarmMapScreen) y con
      // AuditInfoPanel; los otros sitios (AuditMapCard, PDF) también
      // se alinearon a este orden.
      render: (_: any, record: AuditPlot) =>
        `${record.plantation_frame_row || 0}×${record.plantation_frame_column || 0}`,
      sorter: (a: AuditPlot, b: AuditPlot) =>
        (a.plantation_frame_row * a.plantation_frame_column) - (b.plantation_frame_row * b.plantation_frame_column),
    },
    {
      title: t("audit.num_plants_short", "Plantas"),
      dataIndex: "plants",
      key: "plants",
      width: 90,
      render: (value: any) => formatNumber(parseInt(value), 0),
      align: "right" as const,
      sorter: (a: AuditPlot, b: AuditPlot) => (parseInt(a.plants as any) || 0) - (parseInt(b.plants as any) || 0),
    },
    {
      title: t("audit.estimated_production_short", "Aforo (kg)"),
      dataIndex: "kg_plot",
      key: "kg_plot",
      width: 110,
      render: (value: any) => formatNumber(parseFloat(value) || 0, 2),
      align: "right" as const,
      sorter: (a: AuditPlot, b: AuditPlot) => (parseFloat(a.kg_plot as any) || 0) - (parseFloat(b.kg_plot as any) || 0),
    },
    // Photos column intentionally omitted — the audit-detail
    // "Galería de fotos" tab already lists every plot's gallery
    // and exposes the same per-plot view.
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      fixed: "right" as const,
      width: 120,
      render: (_: any, record: AuditPlot) => {
        // "Dar de baja" es la acción de ciclo de vida (parcela
        // arrancada / desaparecida) y "Eliminar" la de limpieza
        // (parcela metida por error). Antes eran mutuamente
        // excluyentes según `precharged`, lo que dejaba sin opción
        // de baja a las parcelas creadas desde Excel en fincas sin
        // PlotReal previa. Ahora las mostramos ambas y el backend
        // materializa la PlotReal canónica al vuelo cuando hace
        // falta — el botón de baja solo se desactiva si la auditoría
        // está cerrada o si ya está dada de baja.
        const plotRealStatus = (record as any).plot_real_status;
        const plotRealClosedHere =
          (record as any).plot_real_closed_in_audit === audit?.id;
        const alreadyClosed = plotRealStatus === "closed";
        return (
        <Space size="small">
          {/* In aforo audits every plot is precharged from the
              previous Completa audit. The auditor reviews + adds
              production via the 3-step wizard instead of editing
              the standalone form. */}
          {isAforoAudit ? (
            <Button
              size="small"
              type="primary"
              ghost
              onClick={() =>
                navigate(`/audits/${audit?.id}/plots/${record.id}/review`, {
                  state: { fromPlanningId, planningName },
                })
              }
              disabled={isReadOnly || alreadyClosed}
              title={
                t("plot.review", "Revisión") +
                (record.kg_plot ? "" : " · " + t("plot.review_pending", "pendiente"))
              }
            >
              {t("plot.review", "Revisión")}
            </Button>
          ) : (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditPlot(record)}
              disabled={isReadOnly || alreadyClosed}
              title={t("common.edit") || "Editar"}
            />
          )}
          <Button
            size="small"
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => {
              setPlotForClose(record);
              setPlotCloseReason("");
              setPlotCloseModalOpen(true);
            }}
            // Solo bloqueamos cuando la auditoría está cerrada o
            // cuando la baja se hizo *en este mismo audit*. Si la
            // PlotReal ya está closed por otro audit la dejamos
            // clicable: el BE estampa `reviewed_at` para que el
            // finalize no lo flage como pendiente, sin reabrir ni
            // pisar el closed_in_audit canónico.
            disabled={isReadOnly || (alreadyClosed && plotRealClosedHere)}
            title={
              alreadyClosed && plotRealClosedHere
                ? t("plot.close_already_here", "Parcela dada de baja en esta auditoría")
                : alreadyClosed
                ? t(
                    "plot.close_confirm_already",
                    "Parcela ya dada de baja en otra auditoría — confirmar para registrarla como revisada aquí",
                  )
                : t("plot.close", "Dar de baja parcela")
            }
          />
          <Popconfirm
            title={t("plot.delete_confirm") || "¿Está seguro de eliminar esta parcela?"}
            description={t("plot.delete_warning") || "Esta acción no se puede deshacer y eliminará todas las fotos asociadas."}
            onConfirm={() => handleDeletePlot(parseInt(record.id))}
            okText={t("common.yes") || "Sí"}
            cancelText={t("common.no") || "No"}
            okButtonProps={{ danger: true }}
            disabled={isReadOnly}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={isReadOnly}
              title={t("common.delete") || "Eliminar"}
            />
          </Popconfirm>
        </Space>
        );
      },
    },
  ];

  // Sample handlers
  const handleCreateSample = () => {
    setEditingSample(null);
    sampleForm.resetFields();
    setIsControlPoint(false);
    setSampleModalVisible(true);
  };

  const handleEditSample = (sample: any) => {
    setEditingSample(sample);
    setIsControlPoint(sample.is_control_point || false);

    // The audit-detail samples_summary returns `control_point` (CP id)
    // already resolved from the underlying review. Keep the legacy
    // fallback for robustness.
    let controlPointId = sample.control_point;
    if (!controlPointId && sample.control_point_review) {
      const review = sample.control_point_review;
      controlPointId = review.control_point || review;
    }

    // Resolve audit_plot id from the sample's plot_name so the Select
    // shows the right option (samples_summary returns the plot name,
    // not its id, for backwards compatibility with the table column).
    const matchingPlot = auditPlots.find((p) => p.name === sample.plot_name);

    sampleForm.setFieldsValue({
      code: sample.code,
      audit_plot: matchingPlot?.id ?? undefined,
      waypoint: sample.waypoint,
      is_control_point: sample.is_control_point || false,
      control_point: controlPointId || undefined,
      notes: sample.notes ?? "",
    });
    setSampleModalVisible(true);
  };

  // ---- Legacy sample handlers (admin only) --------------------------
  //
  // Las muestras legacy viven en `AuditLaboratorySample` y no tienen un
  // ViewSet completo. Editamos vía los endpoints admin creados en
  // `backend/apps/audits_app/views/legacy_sample_admin.py`.

  const handleEditLegacySample = (sample: any) => {
    setLegacyEditingSample(sample);
    legacyForm.setFieldsValue({
      code: sample.code ?? sample.lab_code ?? "",
      plot_name: sample.plot_name ?? "",
      position_waypoint: sample.waypoint ?? sample.position_waypoint ?? "",
    });
    setLegacyEditVisible(true);
  };

  const handleSaveLegacySample = async (values: any) => {
    if (!legacyEditingSample) return;
    setSavingLegacy(true);
    try {
      const resp = await apiClient.patch(
        `/legacy-samples/${legacyEditingSample.id}/`,
        {
          code: (values.code || "").trim().toUpperCase(),
          plot_name: (values.plot_name || "").trim(),
          position_waypoint: (values.position_waypoint || "").trim(),
        },
      );
      if (resp?.data?.noop) {
        message.info(
          t(
            "audit.sample.legacy_noop",
            "Nada que cambiar en la muestra.",
          ),
        );
      } else {
        message.success(
          t(
            "audit.sample.legacy_updated",
            "Muestra actualizada correctamente.",
          ),
        );
      }
      setLegacyEditVisible(false);
      legacyForm.resetFields();
      setLegacyEditingSample(null);
      refetch();
      bumpHistory();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.originalError?.response?.data?.detail ||
        err?.message ||
        t(
          "audit.sample.legacy_update_error",
          "No se pudo guardar la muestra.",
        );
      message.error(detail);
    } finally {
      setSavingLegacy(false);
    }
  };

  const handleDeleteLegacySample = async (sampleId: string) => {
    try {
      await apiClient.delete(`/legacy-samples/${sampleId}/`);
      message.success(
        t(
          "audit.sample.legacy_deleted",
          "Muestra histórica eliminada.",
        ),
      );
      refetch();
      bumpHistory();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.originalError?.response?.data?.detail ||
        err?.message ||
        t(
          "audit.sample.legacy_delete_error",
          "No se pudo eliminar la muestra.",
        );
      message.error(detail);
    }
  };

  const handleRelookupLegacyWaypoint = async (sampleId: string) => {
    setRelookupSampleId(sampleId);
    try {
      const resp = await apiClient.post(
        `/legacy-samples/${sampleId}/relookup-waypoint/`,
      );
      const data = resp?.data || {};
      const name = data.waypoint_name || "";
      const matched = data.matched_geometry_name || name;
      const wpDate = data.matched_waypoint_date || null;
      if (data.found) {
        // El backend devuelve `match_mode`:
        //   'strict'         → mismo auditor + planning + fecha exacta.
        //   'broad_auditor'  → mismo auditor + planning, cualquier fecha.
        //   'broad_planning' → misma planning, cualquier auditor/fecha.
        // Los dos últimos merecen aviso: el admin debe verificar que la
        // asociación es correcta si el waypoint viene de otro día.
        const mode: string = data.match_mode || "strict";
        const modeLabel =
          mode === "strict"
            ? t(
                "audit.sample.relookup_mode_strict",
                "misma planificación y fecha",
              )
            : mode === "broad_auditor"
              ? t(
                  "audit.sample.relookup_mode_broad_auditor",
                  "misma planificación, otra fecha (revísalo)",
                )
              : t(
                  "audit.sample.relookup_mode_broad_planning",
                  "misma planificación, distinto auditor/fecha (revísalo)",
                );
        modal.success({
          title: t(
            "audit.sample.relookup_found_title",
            "Geometría del waypoint asignada",
          ),
          content: t(
            "audit.sample.relookup_found_desc",
            "Waypoint «{{name}}» — coordenadas ({{x}}, {{y}}). Fuente: {{mode}}{{date}}. Se ha actualizado 1 muestra.",
            {
              name: matched,
              x: (data.coord_x ?? 0).toFixed(6),
              y: (data.coord_y ?? 0).toFixed(6),
              mode: modeLabel,
              date: wpDate
                ? ` (${t(
                    "audit.sample.relookup_wp_date",
                    "archivo del {{date}}",
                    { date: wpDate },
                  )})`
                : "",
            },
          ),
        });
        refetch();
      } else {
        const msg = t(
          "audit.sample.relookup_not_found",
          "No se ha encontrado ningún waypoint «{{name}}» en los archivos de planificación de esta auditoría.",
          { name },
        );
        modal.warning({
          title: t(
            "audit.sample.relookup_not_found_title",
            "Waypoint no encontrado",
          ),
          content: msg,
        });
      }
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.originalError?.response?.data?.detail ||
        err?.message ||
        t(
          "audit.sample.relookup_error",
          "No se pudo ejecutar la búsqueda del waypoint.",
        );
      modal.error({
        title: t(
          "audit.sample.relookup_error_title",
          "Error al buscar el waypoint",
        ),
        content: detail,
      });
    } finally {
      setRelookupSampleId(null);
    }
  };

  // Regex de validación local, misma regla que el backend
  // (5 chars, A-Z/0-9, sin «O» inicial). La replicamos aquí para dar
  // feedback inmediato antes de gastar un round-trip.
  const LAB_CODE_RE = /^[A-NP-Z0-9][A-Z0-9]{4}$/;

  const handleAssignLabCode = async (sampleId: string) => {
    const value = (labCodeDraft || "").trim().toUpperCase();
    if (!value) {
      message.error(t("sample.lab_code.empty", "Introduce un código de laboratorio."));
      return;
    }
    if (!LAB_CODE_RE.test(value)) {
      message.error(
        t(
          "sample.lab_code.invalid_format",
          "El código debe tener 5 caracteres (letras mayúsculas o dígitos) y no puede empezar por «O».",
        ),
      );
      return;
    }
    setSavingLabCode(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      await apiClient.patch(`/v1/samples/${sampleId}/assign_lab_code/`, {
        lab_code: value,
      });
      message.success(
        t("sample.lab_code.saved", "Código de laboratorio guardado."),
      );
      setEditingLabCodeId(null);
      setLabCodeDraft("");
      refetch();
    } catch (err: any) {
      // Show the concrete backend error — the important one here is
      // the uniqueness violation (another sample already has this code).
      const resp = err?.originalError?.response?.data;
      const detail =
        (resp && (resp.lab_code || resp.detail)) ||
        err?.message ||
        t("sample.lab_code.error", "No se pudo guardar el código.");
      const text = Array.isArray(detail) ? detail.join(" ") : String(detail);
      message.error(text);
    } finally {
      setSavingLabCode(false);
    }
  };

  const handleDeleteSample = async (sampleId: string) => {
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      await apiClient.delete(`/samples/${sampleId}/`);
      message.success('Muestra eliminada correctamente');
      refetch();
      bumpMap();
      // Sample counts feed into the farm history timeline — refresh
      // so the row representing this audit shows the new count.
      bumpHistory();
    } catch (error: any) {
      const errorMessage = error.originalError?.response?.data?.error || 
                          error.message || 
                          'Error al eliminar la muestra';
      message.error(errorMessage);
    }
  };

  const handleSaveSample = async (values: any) => {
    if (!audit?.id) return;

    setIsSavingSample(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");

      // Backend `SampleWriteSerializer` accepts the legacy aliases
      // (`code`, `waypoint`, `is_control_point`, `control_point`) and
      // resolves the `control_point_review` from `(audit, control_point)`.
      const sampleData: Record<string, any> = {
        code: values.code,
        waypoint: values.waypoint,
        is_control_point: values.is_control_point || false,
        audit: audit.id,
        notes: values.notes ?? "",
      };
      if (values.is_control_point) {
        sampleData.control_point = values.control_point;
        sampleData.audit_plot = null;
      } else {
        sampleData.audit_plot = values.audit_plot;
        sampleData.control_point = null;
      }

      if (editingSample) {
        // Update existing sample
        await apiClient.patch(`/samples/${editingSample.id}/`, sampleData);
        message.success('Muestra actualizada correctamente');
      } else {
        // Create new sample
        await apiClient.post('/samples/', sampleData);
        message.success('Muestra creada correctamente');
      }

      setSampleModalVisible(false);
      sampleForm.resetFields();
      refetch();
      // Same reasoning as in handleDeleteSample — refresh the farm
      // history so its sample count reflects the change.
      bumpHistory();
    } catch (error: any) {
      // El apiClient interceptor transforma el error, necesitamos acceder a originalError
      const originalError = error.originalError || error;
      const responseData = originalError.response?.data;
      
      // Intentar obtener el mensaje de error de diferentes formas
      let errorMessage = responseData?.error || 
                        responseData?.message ||
                        responseData?.detail ||
                        error.message ||
                        (editingSample ? 'Error al actualizar la muestra' : 'Error al crear la muestra');
      
      // Si errorMessage es un objeto, convertirlo a string
      if (typeof errorMessage === 'object') {
        errorMessage = JSON.stringify(errorMessage);
      }
      
      // Si es un error de código duplicado, mostrar en el campo del formulario
      if (errorMessage.includes('Ya existe')) {
        sampleForm.setFields([
          {
            name: 'code',
            errors: [errorMessage],
          },
        ]);
      }
      
      // Siempre mostrar el mensaje de error
      message.error(errorMessage);
    } finally {
      setIsSavingSample(false);
    }
  };

  // Samples columns
  // Aplicamos los filtros locales de buscador (texto) + resultado
  // (dropdown). Buscamos por code, lab_code, sample_reference, waypoint
  // y plot_name — todo lo que un auditor pueda tener en la cabeza al
  // buscar. Para el filtro por resultado, comparamos contra
  // consensus_state (v2, más fiable) y contra state (legacy) para
  // cubrir ambos tipos de muestra en la misma tabla.
  const filteredSamples = useMemo<Sample[]>(() => {
    const rows = audit?.samples_summary ?? [];
    const q = sampleSearchText.trim().toLowerCase();
    return rows.filter((s: any) => {
      if (q) {
        const hay = [
          s.code, s.lab_code, s.sample_reference, s.waypoint, s.plot_name,
        ]
          .filter(Boolean)
          .some((f: string) => String(f).toLowerCase().includes(q));
        if (!hay) return false;
      }
      if (sampleResultFilter) {
        const consensus = (s.consensus_state || "").toUpperCase();
        const state = String(s.state || "").toUpperCase();
        // legacy `state` puede ser el ID numérico — comparamos también
        // contra `state_name` normalizado a "TANGO" / "NO_TANGO" (el
        // serializer legacy ya emite ese formato).
        const stateName = (s.state_name || "").toUpperCase().replace(/\s+/g, "_");
        if (
          consensus !== sampleResultFilter
          && state !== sampleResultFilter
          && stateName !== sampleResultFilter
        ) return false;
      }
      return true;
    });
  }, [audit?.samples_summary, sampleSearchText, sampleResultFilter]);

  const sampleColumns = [
    {
      title: t("audit.lab_code") || "Código Laboratorio",
      dataIndex: "code",
      key: "code",
      // Sort by lab_code first (real code beats auto-generated
      // sample_reference), fall back to `code` for legacy rows.
      sorter: (a: Sample, b: Sample) =>
        (a.lab_code || a.code || "").localeCompare(b.lab_code || b.code || ""),
      render: (_value: string, record: Sample) => {
        const isLegacy = record.source === "legacy";
        const isEditing = editingLabCodeId === String(record.id);
        // Legacy rows are read-only — no assign_lab_code endpoint for
        // the old model. Also block edits when the audit is finalised.
        const canEdit = !isLegacy && !isReadOnly;

        if (isEditing) {
          return (
            <Space size={4}>
              <Input
                size="small"
                value={labCodeDraft}
                autoFocus
                maxLength={5}
                onChange={(e) =>
                  setLabCodeDraft(e.target.value.toUpperCase())
                }
                onPressEnter={() => handleAssignLabCode(String(record.id))}
                placeholder="AB123"
                style={{ width: 90, textTransform: "uppercase" }}
              />
              <Button
                size="small"
                type="primary"
                loading={savingLabCode}
                onClick={() => handleAssignLabCode(String(record.id))}
              >
                {t("common.save", "Guardar")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditingLabCodeId(null);
                  setLabCodeDraft("");
                }}
              >
                {t("common.cancel", "Cancelar")}
              </Button>
            </Space>
          );
        }

        // No lab_code yet → show a distinct "Asignar" CTA. `code` may
        // still carry the auto-generated `sample_reference` from the
        // mobile app; we hide it here so the auditor isn't tempted to
        // treat that reference as the real lab code.
        if (!record.lab_code) {
          return canEdit ? (
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingLabCodeId(String(record.id));
                setLabCodeDraft("");
              }}
            >
              {t("sample.lab_code.assign", "Asignar código")}
            </Button>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          );
        }

        // Has a real lab_code — show it and allow editing to fix typos.
        return (
          <Space size={4}>
            <strong>{record.lab_code}</strong>
            {canEdit && (
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingLabCodeId(String(record.id));
                  setLabCodeDraft(record.lab_code ?? "");
                }}
                title={t("common.edit", "Editar")}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: t("audit.plot_name") || "Parcela",
      dataIndex: "plot_name",
      key: "plot_name",
      // Cuando la muestra es de comprobación de material nuevo
      // injertado, aparte del nombre de la parcela pintamos un tag
      // "Material nuevo" para que quien revise la auditoría desde la
      // oficina distinga estas muestras a un vistazo.
      render: (_: any, record: Sample) => (
        <Space size={4} wrap>
          <span>{record.plot_name}</span>
          {record.planting_label ? (
            <Tag
              color="geekblue"
              style={{ marginRight: 0 }}
              title={t(
                "audit.sample.planting_tooltip",
                "Planting de la parcela doblada",
              )}
            >
              {record.planting_label}
            </Tag>
          ) : null}
          {record.is_new_grafting_check ? (
            <Tag color="purple" style={{ marginRight: 0 }}>
              {t("audit.sample.new_grafting_short", "Material nuevo")}
            </Tag>
          ) : null}
        </Space>
      ),
      sorter: (a: Sample, b: Sample) => (a.plot_name || "").localeCompare(b.plot_name || ""),
    },
    {
      title: t("audit.waypoint") || "Waypoint",
      dataIndex: "waypoint",
      key: "waypoint",
      // En muestras de material nuevo no hay waypoint por definición;
      // dejamos claro con un "—" en gris en lugar de un vacío raro.
      // Si la muestra tiene notas, mostramos un tag 📝 con tooltip
      // que revela el contenido — pistá para el auditor sin ampliar
      // la tabla con una columna dedicada.
      render: (value: string, record: Sample) => (
        <Space size={4}>
          {record.is_new_grafting_check ? (
            <Typography.Text type="secondary">—</Typography.Text>
          ) : (
            <span>{value || "-"}</span>
          )}
          {record.notes ? (
            <Tooltip title={record.notes}>
              <Tag color="blue" style={{ marginRight: 0, cursor: "help" }}>
                📝
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
      sorter: (a: Sample, b: Sample) => (a.waypoint || "").localeCompare(b.waypoint || ""),
    },
    {
      title: t("audit.result") || "Resultado",
      dataIndex: "state_name",
      key: "state_name",
      sorter: (a: Sample, b: Sample) => (a.state_name || "").localeCompare(b.state_name || ""),
      render: (value: string, record: Sample) => {
        // Muestras v2 pueden tener múltiples SampleResult (contranálisis
        // + placas adicionales). Cuando results_count > 1 marcamos con
        // un tag "+N" en morado. El tooltip enseña la lista de placas
        // ordenadas cronológicamente con su tipo (primary/recheck/csic)
        // y estado, para que el auditor no tenga que abrir el detalle.
        const count = record.results_count ?? 0;
        const results = record.results ?? [];
        const formatDate = (iso: string | null) => {
          if (!iso) return "";
          try {
            return new Date(iso).toLocaleDateString("es-ES", {
              day: "2-digit", month: "2-digit", year: "2-digit",
            });
          } catch {
            return "";
          }
        };
        const STATE_DOT: Record<string, string> = {
          TANGO: "#52c41a",
          NO_TANGO: "#ff4d4f",
          NA: "#bfbfbf",
          NC: "#40a9ff",
          MIXED: "#722ed1",
          OTHER_VARIETY: "#eb2f96",
          PENDIENTE: "#faad14",
        };
        const tooltipContent = results.length > 0 ? (
          <div style={{ maxWidth: 320, fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {t("audit.sample.results_list", "Placas analizadas")}
            </div>
            {results.map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 8, height: 8, borderRadius: "50%",
                    background: STATE_DOT[r.state] ?? "#8c8c8c",
                    flexShrink: 0,
                  }}
                />
                <span style={{ textTransform: "capitalize", opacity: 0.85 }}>
                  {r.result_type_label || r.result_type}
                </span>
                <span>·</span>
                <strong>{r.state_label || r.state}</strong>
                {r.state === "OTHER_VARIETY" && r.detected_variety ? (
                  <span style={{ opacity: 0.85 }}>→ {r.detected_variety}</span>
                ) : null}
                {r.plate_code ? (
                  <span style={{ opacity: 0.7 }}>· {r.plate_code}</span>
                ) : null}
                {r.analyzed_at ? (
                  <span style={{ opacity: 0.65, marginLeft: "auto" }}>
                    {formatDate(r.analyzed_at)}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          t(
            "audit.sample.multi_results_tooltip",
            `${count} análisis (contranálisis o múltiples placas)`,
          )
        );
        return (
          <Space size={4} wrap>
            <span>{value || "-"}</span>
            {count > 1 ? (
              <Tooltip title={tooltipContent} placement="topLeft">
                <Tag
                  color="purple"
                  style={{ marginRight: 0, cursor: "help" }}
                >
                  +{count - 1}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: Sample) => {
        // Legacy samples viven en `AuditLaboratorySample`. Para el
        // resto de roles siguen siendo solo-lectura (no hay flujo de
        // sincronización que las actualice desde el móvil). El admin
        // sí tiene un CRUD limitado (`/api/legacy-samples/{id}/`) más
        // el botón de re-lookup del waypoint para arreglar códigos y
        // volver a asociar la geometría cuando la muestra se creó
        // antes de subir el archivo waypoint.
        const isLegacy = record.source === 'legacy';
        if (isLegacy) {
          if (!isAdmin) {
            return (
              <Space size="small">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled
                  title={t(
                    "audit.sample.historical_readonly",
                    "Muestra histórica — solo lectura",
                  )}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled
                  title={t(
                    "audit.sample.historical_readonly",
                    "Muestra histórica — solo lectura",
                  )}
                />
              </Space>
            );
          }
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditLegacySample(record)}
                title={t(
                  "audit.sample.legacy_edit",
                  "Editar muestra histórica (admin)",
                )}
              />
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                loading={relookupSampleId === record.id}
                onClick={() =>
                  handleRelookupLegacyWaypoint(String(record.id))
                }
                title={t(
                  "audit.sample.legacy_relookup_waypoint",
                  "Volver a buscar geometría del waypoint (admin)",
                )}
              />
              <Popconfirm
                title={t(
                  "audit.sample.delete_confirm",
                  "¿Está seguro de eliminar esta muestra?",
                )}
                onConfirm={() =>
                  handleDeleteLegacySample(String(record.id))
                }
                okText={t("common.yes", "Sí")}
                cancelText={t("common.no", "No")}
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  title={t(
                    "audit.sample.legacy_delete",
                    "Eliminar muestra histórica (admin)",
                  )}
                />
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditSample(record)}
              disabled={isSamplesReadOnly}
              title={
                isReadOnly && isAdmin
                  ? t(
                      "audit.sample.admin_edit_locked",
                      "Editar muestra (auditoría cerrada — sólo admin)",
                    )
                  : t("common.edit", "Editar")
              }
            />
            <Popconfirm
              title={t(
                "audit.sample.delete_confirm",
                "¿Está seguro de eliminar esta muestra?",
              )}
              onConfirm={() => handleDeleteSample(String(record.id))}
              okText={t("common.yes", "Sí")}
              cancelText={t("common.no", "No")}
              okButtonProps={{ danger: true }}
              disabled={isSamplesReadOnly}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isSamplesReadOnly}
                title={t("common.delete", "Eliminar")}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Function to determine row color based on sample state and control point
  const getRowClassName = (record: Sample) => {
    const stateName = record.state_name?.toUpperCase().trim();
    
    // NC or NA - yellow
    if (stateName === 'NC' || stateName === 'NA') {
      return 'sample-row-warning';
    }
    
    // Check for "Es Tango" or "TANGO"
    const isTango = stateName?.includes('TANGO') && !stateName?.includes('NO');
    // Check for "No es Tango" or "NO TANGO"
    const isNoTango = stateName?.includes('NO') && stateName?.includes('TANGO');
    
    // Control point (is_control_point = true)
    if (record.is_control_point) {
      if (isTango) {
        return 'sample-row-success'; // Green
      } else if (isNoTango) {
        return 'sample-row-info'; // Blue
      }
    } else {
      // Not control point (is_control_point = false)
      if (isTango) {
        return 'sample-row-info'; // Blue
      } else if (isNoTango) {
        return 'sample-row-error'; // Red
      }
    }
    
    return '';
  };

  // Get navigation context from location state
  //
  // El contexto de "de dónde vino el usuario" (una planificación, una
  // finca...) viaja como `location.state` al entrar en el detalle de la
  // auditoría. Pero al saltar a una sub-página (revisar parcela, punto
  // de control, cultivo colindante) y volver, ese state se pierde:
  // React Router no lo restaura si el sub-page navega con
  // `navigate('/audits/X')` en vez de con `navigate(-1)`, y el sub-page
  // no siempre puede/quiere hacer `-1` (rompería la promesa de "guardar
  // y volver al detalle").
  //
  // Para que el breadcrumb y el botón "Volver" del detalle sigan
  // apuntando a la planificación original tras esos round-trips,
  // persistimos el contexto en `sessionStorage` keyed por auditId. La
  // primera vez que llegamos con state lo guardamos; en las visitas
  // posteriores lo recuperamos. Como es sessionStorage se limpia al
  // cerrar la pestaña — no ensucia el navegador a largo plazo.
  const stateFromPlanningId = (location.state as any)?.fromPlanningId;
  const stateFromPlanningName = (location.state as any)?.planningName;
  // Query string original (`?page=…&pageSize=…&tab=…`) del planning de
  // origen — sirve para que al volver a la tabla el usuario aterrice
  // exactamente en la página/filtros/tab que dejó. Persistimos en
  // sessionStorage junto al resto del contexto de navegación.
  const stateFromPlanningSearch = (location.state as any)?.fromPlanningSearch;
  // Mismo mecanismo para el origen "listado de auditorías": cuando el
  // usuario llega desde /audits persistimos el search (filtros+orden+
  // paginación de Refine via syncWithLocation) para que al volver no
  // pierda su contexto, igual que ya hacíamos con planning.
  const stateFromAuditsSearch =
    (location.state as any)?.from === "/audits"
      ? (location.state as any)?.listSnapshot?.search ?? ""
      : undefined;
  const auditIdForStorage = audit?.id;
  const navStorageKey = auditIdForStorage
    ? `audit-nav-context-${auditIdForStorage}`
    : null;

  useEffect(() => {
    if (!navStorageKey) return;
    // Solo escribimos cuando llega state nuevo (planning o listado).
    // No pisamos lo que había si el usuario está rebotando entre
    // sub-pages sin volver a pasar por el listado o la planificación.
    if (stateFromPlanningId || stateFromAuditsSearch !== undefined) {
      try {
        sessionStorage.setItem(
          navStorageKey,
          JSON.stringify({
            fromPlanningId: stateFromPlanningId ?? null,
            planningName: stateFromPlanningName ?? null,
            fromPlanningSearch: stateFromPlanningSearch ?? "",
            fromAuditsSearch: stateFromAuditsSearch ?? null,
          }),
        );
      } catch {
        // sessionStorage puede estar deshabilitado (modo privado en
        // algunos navegadores); si pasa, el breadcrumb caerá al
        // fallback en vez de romper.
      }
    }
  }, [
    navStorageKey,
    stateFromPlanningId,
    stateFromPlanningName,
    stateFromPlanningSearch,
    stateFromAuditsSearch,
  ]);

  const persistedNavContext = useMemo(() => {
    if (!navStorageKey) return null;
    try {
      const raw = sessionStorage.getItem(navStorageKey);
      return raw
        ? (JSON.parse(raw) as {
            fromPlanningId?: string | number | null;
            planningName?: string | null;
            fromPlanningSearch?: string | null;
            fromAuditsSearch?: string | null;
          })
        : null;
    } catch {
      return null;
    }
    // Re-leemos también cuando llega state nuevo (por si el usuario
    // navega desde una planificación distinta a la misma auditoría).
  }, [navStorageKey, stateFromPlanningId, stateFromAuditsSearch]);

  const fromPlanningId =
    stateFromPlanningId ?? persistedNavContext?.fromPlanningId ?? undefined;
  const planningName =
    stateFromPlanningName ?? persistedNavContext?.planningName ?? undefined;
  const fromPlanningSearch =
    stateFromPlanningSearch ?? persistedNavContext?.fromPlanningSearch ?? "";
  // null → viene de otro sitio; "" → viene del listado sin filtros;
  // "?page=…" → viene del listado con contexto; undefined → sin dato.
  // El back-button sólo cae a este path si NO viene de planning
  // (planning gana porque es contexto más específico).
  const fromAuditsSearch =
    stateFromAuditsSearch ?? persistedNavContext?.fromAuditsSearch ?? null;

  // Build dynamic breadcrumb based on navigation context
  const getBreadcrumbItems = () => {
    const items: any[] = [
      {
        title: <HomeOutlined />,
        href: "/",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/");
        },
      },
    ];

    if (fromFarmId) {
      // Coming from farm detail
      items.push({
        title: t("farms.title", "Fincas"),
        href: "/farms",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/farms");
        },
      });
      items.push({
        title: audit?.farm?.name || `Finca #${fromFarmId}`,
        href: `/farms/show/${fromFarmId}`,
        onClick: (e: any) => {
          e.preventDefault();
          navigate(`/farms/show/${fromFarmId}`);
        },
      });
    } else if (fromPlanningId) {
      // Coming from planning group — pick the right URL space per role.
      // Preservamos también el `fromPlanningSearch` (?page=…&pageSize=
      // &tab=…) para que el user aterrice en la misma página y filtros
      // que dejó.
      const planningsListUrl = isAuditor
        ? "/auditor/planning-groups"
        : "/planning-groups";
      const planningDetailBase = isAuditor
        ? `/auditor/planning-groups/${fromPlanningId}`
        : `/planning-groups/show/${fromPlanningId}`;
      const planningDetailUrl = `${planningDetailBase}${fromPlanningSearch || ""}`;
      items.push({
        title: t("planning.title", "Planificaciones"),
        href: planningsListUrl,
        onClick: (e: any) => {
          e.preventDefault();
          navigate(planningsListUrl);
        },
      });
      items.push({
        title: planningName || `Planificación #${fromPlanningId}`,
        href: planningDetailUrl,
        onClick: (e: any) => {
          e.preventDefault();
          navigate(planningDetailUrl);
        },
      });
    } else if (isAuditor) {
      // Auditor / coordinator with no upstream context (deep link or
      // refresh). Route the breadcrumb through their plannings list,
      // never through /audits, and — when the audit knows which
      // planning it belongs to — drop the planning name between
      // "Planificaciones" and the audit reference so the auditor can
      // step back into the planning detail with one click.
      items.push({
        title: (
          <>
            <CalendarOutlined /> {t("planning.title", "Planificaciones")}
          </>
        ),
        href: "/auditor/planning-groups",
        onClick: (e: any) => {
          e.preventDefault();
          navigate("/auditor/planning-groups");
        },
      });
      const planningGroup = audit?.planning_group;
      if (planningGroup?.id) {
        const planningDetailUrl = `/auditor/planning-groups/${planningGroup.id}`;
        items.push({
          title: planningGroup.name || `Planificación #${planningGroup.id}`,
          href: planningDetailUrl,
          onClick: (e: any) => {
            e.preventDefault();
            navigate(planningDetailUrl);
          },
        });
      }
    } else {
      // Admin path — show the full audits list crumb.
      items.push({
        title: (
          <>
            <AuditOutlined /> {t("audit.title", "Auditorías")}
          </>
        ),
        href: fromAuditsSearch !== null ? `/audits${fromAuditsSearch}` : "/audits",
        onClick: (e: any) => {
          e.preventDefault();
          navigate(
            fromAuditsSearch !== null ? `/audits${fromAuditsSearch}` : "/audits",
          );
        },
      });
    }

    items.push({
      title: audit?.reference || `Auditoría #${audit?.id}`,
    });

    return items;
  };

  // Botón "Volver" (chevron a la izquierda del título) — por defecto
  // Refine hace `history.back()`, que tras un round-trip por sub-pages
  // (plot review / control point / cultivo colindante) devuelve al
  // formulario y no a la planificación. Sobrescribimos para que siempre
  // apunte al origen semántico: la planificación si tenemos contexto,
  // el listado que corresponda si no.
  const handleGoBackFromShow = () => {
    if (fromPlanningId) {
      const base = isAuditor
        ? `/auditor/planning-groups/${fromPlanningId}`
        : `/planning-groups/show/${fromPlanningId}`;
      // Restauramos la paginación/filtros/tab que el user tenía en la
      // tabla del planning antes de entrar al detalle.
      navigate(`${base}${fromPlanningSearch || ""}`);
      return;
    }
    if (fromFarmId) {
      navigate(`/farms/show/${fromFarmId}`);
      return;
    }
    if (!isAuditor && fromAuditsSearch !== null) {
      // Vuelta al listado de auditorías preservando filtros/sort/
      // paginación (Refine syncWithLocation vive en el search string).
      navigate(`/audits${fromAuditsSearch}`);
      return;
    }
    navigate(isAuditor ? "/auditor/planning-groups" : "/audits");
  };

  return (
    <Show
      isLoading={isLoading}
      contentProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
      // Refine `<Show>` renderiza un antd `<PageHeader>` internamente:
      // `goBack` es solo el ICONO del chevron; el click real dispara
      // `useBack()` (history.back). Pasar un Button con onClick propio
      // no basta — el onBack del PageHeader corre igualmente y genera
      // un doble-nav que confunde al history stack (y a veces revienta
      // con un error que Sentry intenta reportar). El fix es dejar el
      // icono por defecto y sobrescribir SOLO el handler vía
      // `headerProps.onBack`.
      goBack={<ArrowLeftOutlined />}
      headerProps={{ onBack: handleGoBackFromShow }}
      breadcrumb={<Breadcrumb items={getBreadcrumbItems()} />}
      headerButtons={() => {
        // Consolidated actions menu. The audit detail accumulates a
        // lot of contextual actions; keeping them in one Dropdown
        // makes the header readable and leaves room for the back
        // button + the act primary action on the right.
        const SUPPORTED_TYPES = [1, 2, 3, 5];
        const bundleSupported = SUPPORTED_TYPES.includes(
          audit?.audit_type?.id ?? -1,
        );
        const menuItems: NonNullable<MenuProps["items"]> = [
          // "Crear Incidencia", "Ver Mapa" and "Subir Fotos" used to
          // live here but they were redundant: incidencia + fotos
          // already have their own tab/buttons inside the detail and
          // the embedded map card replaces the full-screen "Ver
          // Mapa". Removed to declutter the dropdown.
          ...(isAdmin
            ? [
                {
                  key: "contacts",
                  icon: <TeamOutlined />,
                  label: t("contacts.title") || "Contactos del Productor",
                  onClick: handleViewProducerContacts,
                },
                { type: "divider" as const },
              ]
            : []),
          {
            key: "upload-data",
            icon: <UploadOutlined />,
            label: t("audit.upload_data") || "CARGAR DATOS AUDITORÍA",
            disabled: isReadOnly,
            onClick: handleUploadAuditData,
          },
          {
            key: "download-bundle",
            icon: <CloudDownloadOutlined />,
            label: t("audit.download_bundle", "Descargar Excel + Mapa"),
            // Read-only export — stays available even on finalised audits.
            disabled: !bundleSupported || downloadingBundle,
            onClick: handleDownloadBundle,
          },
          { type: "divider" as const },
          // When an acta already exists, expose two separate actions
          // ("Ver" + "Reemplazar") so the auditor can update it
          // without first deleting. The backend's upload action
          // already deletes the old file when a new one is saved, so
          // "Reemplazar" is just a re-upload that overwrites cleanly.
          ...(audit?.has_audit_act
            ? [
                {
                  key: "view-act",
                  icon: <FilePdfOutlined />,
                  label: t("audit.view_act") || "Ver Acta",
                  onClick: handleViewAuditAct,
                },
                ...(canGenerateActa
                  ? [
                      {
                        key: "regenerate-act",
                        icon: generatingActa ? (
                          <LoadingOutlined spin />
                        ) : (
                          <FilePdfOutlined />
                        ),
                        label: generatingActa
                          ? t("audit.generating_act", "Generando acta…")
                          : t("audit.regenerate_act", "Regenerar acta"),
                        disabled: generatingActa,
                        onClick: handleGenerateActa,
                      },
                    ]
                  : []),
                {
                  key: "replace-act",
                  icon: <UploadOutlined />,
                  label: t("audit.replace_act") || "Reemplazar Acta",
                  // Permitimos subir la versión firmada en papel
                  // también en `submitted` — el auditor cierra la
                  // audit, pide firma del productor por correo, y
                  // cuando la recibe la sube. Sólo `finalized`
                  // (lock del backend) bloquea el upload.
                  disabled: isFinalized,
                  onClick: handleUploadAuditAct,
                },
              ]
            : [
                ...(canGenerateActa
                  ? [
                      {
                        key: "generate-act",
                        icon: generatingActa ? (
                          <LoadingOutlined spin />
                        ) : (
                          <FilePdfOutlined />
                        ),
                        label: generatingActa
                          ? t("audit.generating_act", "Generando acta…")
                          : t("audit.generate_act", "Generar acta"),
                        disabled: generatingActa,
                        onClick: handleGenerateActa,
                      },
                    ]
                  : []),
                {
                  key: "upload-act",
                  icon: <UploadOutlined />,
                  label: t("audit.upload_act") || "Cargar Acta",
                  // Mismo criterio que `replace-act`: no bloqueamos
                  // en `submitted` para que el auditor pueda subir el
                  // acta firmada en papel tras recibirla.
                  disabled: isFinalized,
                  onClick: handleUploadAuditAct,
                },
              ]),
          // El acta "de cierre" (`closure_report_file`) era el PDF
          // frontend-rendered que se guardaba al finalizar la audit —
          // conceptualmente redundante con el acta SGS (`audit_act_file`)
          // que ahora se muestra en "Ver acta". La opción de descarga
          // generaba confusión al admin ("¿cuál es la buena?"), así
          // que la quitamos del dropdown. El fichero sigue almacenado
          // para quien lo necesite via API (/download-closure-report/).
          // Cancel audit (admin only, audit still in "Planificada").
          // Lives at the bottom of the dropdown, separated by a
          // divider so it doesn't sit next to the everyday actions.
          ...(isAdmin && canCancelAudit
            ? [
                { type: "divider" as const },
                {
                  key: "cancel-audit",
                  icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
                  label: (
                    <span style={{ color: "#ff4d4f" }}>
                      {t("audit.cancel_title", "Cancelar auditoría")}
                    </span>
                  ),
                  onClick: () => setCancelModalOpen(true),
                },
              ]
            : []),
        ];
        return (
          <>
            {/* Finalize: confirm → generate the closure PDF → POST
                to /audits/{id}/finalize/ (multipart with the PDF) →
                refetch. The state tag under the title flips to
                "Finalizada" once the request returns. The preview-
                only button was removed: every render now goes
                through finalize, so a draft download isn't useful. */}
            {/* Two visual states:
                  • Active (audit not yet finalised): bold green
                    primary button, our brand-green inline.
                  • Disabled (audit finalised or no audit yet): keep
                    the same green-themed Tag look instead of antd's
                    grey disabled style, which was hard to read.
                  Implementation: when `disabled` we drop the
                  `type="primary"` so antd doesn't apply the
                  disabled-primary grey, and we use a subtle
                  green-tinted fill + outline that reads as
                  "already done". */}
            {/* Botonera del estado del cierre. Depende del rol:
                 · Auditor (isAuditor && !isAdmin):
                   - performed: "Cerrar auditoría" → submit-for-review
                   - submitted: "Reabrir para editar" (auditor puede
                     retocar; se genera nueva submission al re-cerrar)
                   - finalized: chip verde "Finalizada" (solo lectura)
                 · Admin:
                   - performed: nada (espera al auditor)
                   - submitted: "Finalizar" (verde) + "Devolver" (rojo)
                   - finalized: chip verde "Finalizada"
                 Un usuario que sea admin Y auditor (raro) ve la
                 botonera admin (isAdmin gana). */}
            {isFinalized ? (
              <Space size="small">
                <Button
                  icon={<CheckCircleOutlined style={{ color: "#0a8a18" }} />}
                  style={{
                    background: "#eaf6ec",
                    borderColor: "#0a8a18",
                    color: "#0a8a18",
                    fontWeight: 600,
                    cursor: "default",
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  {t("audit.finalized", "Auditoría finalizada")}
                </Button>
                {isAdmin && (
                  <Button
                    icon={<WarningOutlined />}
                    onClick={openReopenModal}
                    title={t(
                      "audit.reopen_hint",
                      "Reabrir la auditoría para corregir errores. Deberás finalizarla de nuevo cuando termines.",
                    )}
                  >
                    {t("audit.reopen", "Reabrir")}
                  </Button>
                )}
              </Space>
            ) : isAdmin ? (
              isSubmitted ? (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={generatingReport}
                    disabled={!audit?.id}
                    onClick={() => handleFinalizeAudit()}
                    style={{ background: "#0a8a18", borderColor: "#0a8a18" }}
                  >
                    {t("audit.finalize", "Finalizar auditoría")}
                  </Button>
                  <Button
                    danger
                    icon={<WarningOutlined />}
                    disabled={!audit?.id}
                    onClick={handleReturnToAuditor}
                  >
                    {t("audit.return_to_auditor", "Devolver al auditor")}
                  </Button>
                </>
              ) : (
                // Admin viendo una auditoría en `performed` (auditor
                // no la ha cerrado aún): atajo "Cerrar y finalizar"
                // que compone submit-for-review + finalize en un
                // solo click. Sirve para auditor de baja, olvido de
                // cerrar, o cierre en oficina por parte del admin.
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={generatingReport}
                  disabled={!audit?.id}
                  onClick={() => handleFinalizeAudit(true)}
                  style={{ background: "#0a8a18", borderColor: "#0a8a18" }}
                  title={t(
                    "audit.admin_close_hint",
                    "Cierra la auditoría por el auditor y la finaliza en un solo paso",
                  )}
                >
                  {t(
                    "audit.admin_close_and_finalize",
                    "Cerrar y finalizar (admin)",
                  )}
                </Button>
              )
            ) : isSubmitted ? (
              // Auditor con la auditoría cerrada: puede reabrir para
              // corregir. No verá el "Finalizar" (admin-only).
              <Button
                icon={<WarningOutlined />}
                disabled={!audit?.id}
                onClick={handleReopenSubmission}
              >
                {t("audit.reopen_for_edit", "Reabrir para editar")}
              </Button>
            ) : (
              // Auditor con la auditoría abierta: pulsa "Cerrar
              // auditoría" para congelar snapshot y enviarla al
              // admin. Sustituye al viejo "Finalizar auditoría".
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={!audit?.id}
                onClick={handleSubmitForReview}
                style={{ background: "#1677ff", borderColor: "#1677ff" }}
              >
                {t("audit.submit_for_review", "Cerrar auditoría")}
              </Button>
            )}
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button type="primary" loading={generatingActa}>
                {generatingActa
                  ? t("audit.generating_act", "Generando acta…")
                  : t("common.actions") || "Acciones"}{" "}
                {!generatingActa && <DownOutlined />}
              </Button>
            </Dropdown>
          </>
        );
      }}
    >
      {/* Banner intermedio: auditoría cerrada por el auditor pero
           pendiente de revisión del admin. Con isAdmin le explicamos
           qué acciones puede hacer; con isAuditor le explicamos que
           está en modo lectura y que puede "Reabrir" si necesita. */}
      {audit && isSubmitted && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: -12, marginBottom: 12 }}
          icon={<CheckCircleOutlined style={{ color: "#1677ff" }} />}
          message={t(
            "audit.submitted_banner_title",
            "Auditoría cerrada por el auditor — pendiente de revisión",
          )}
          description={
            isAdmin
              ? t(
                  "audit.submitted_banner_admin",
                  "El auditor ha cerrado la auditoría y ha guardado una copia inmutable del estado. Revisa los datos y pulsa 'Finalizar' cuando esté validada, o 'Devolver al auditor' con motivo si falta algo.",
                )
              : t(
                  "audit.submitted_banner_auditor",
                  "Has cerrado la auditoría — está en modo lectura hasta que el administrador la finalice. Si necesitas retocar algún dato, pulsa 'Reabrir para editar'.",
                )
          }
        />
      )}

      {/* Lock banner: only when the backend reports the audit as
          finalised. Sticks above the tag strip + everything else so
          the auditor sees the read-only state before scrolling. */}
      {audit && isFinalized && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: -12, marginBottom: 12 }}
          icon={<CheckCircleOutlined style={{ color: "#0a8a18" }} />}
          message={t(
            "audit.locked_banner_title",
            "Auditoría finalizada — sólo lectura",
          )}
          description={
            <>
              {t(
                "audit.locked_banner_body",
                "Esta auditoría está cerrada. Las acciones de edición están deshabilitadas para auditores y coordinadores. Si necesitas modificar algún dato, contacta con un administrador.",
              )}
              {(audit as any)?.verification_code && (
                <div style={{ marginTop: 4 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("audit.verification_code", "Código de verificación")}:{" "}
                    <code>{(audit as any).verification_code}</code>
                  </Typography.Text>
                </div>
              )}
            </>
          }
        />
      )}
      {/* AforoPushCard se renderiza DENTRO del segmento "Datos" para
          que el Resumen quede limpio. En Resumen solo mostramos un
          Tag de estado (sincronizado o pendiente). */}
      {/* Quick-glance tags right under the Show title. Negative
          marginTop pulls them up tight against the title, removing
          the default body padding gap. */}
      {audit && (
        <Space size={8} wrap style={{ marginBottom: 12 }}>
          {audit.reference && (
            <Tag color="geekblue" style={{ fontSize: 13, padding: "2px 10px" }}>
              <strong>{t("audit.reference", "Referencia")}:</strong>{" "}
              {audit.reference}
            </Tag>
          )}
          {audit.audit_type?.name && (
            <Tag color="cyan" style={{ fontSize: 13, padding: "2px 10px" }}>
              <strong>{t("audit.type", "Tipo")}:</strong> {audit.audit_type.name}
            </Tag>
          )}
          {audit.audit_state?.name && (
            <Tag
              color={getAuditStateColor(audit.audit_state.name)}
              style={{ fontSize: 13, padding: "2px 10px" }}
            >
              <strong>{t("audit.state", "Estado")}:</strong> {audit.audit_state.name}
            </Tag>
          )}
          {audit.auditor_user?.name && (
            <Tag color="blue" style={{ fontSize: 13, padding: "2px 10px" }}>
              <strong>{t("audit.auditor", "Auditor")}:</strong>{" "}
              {audit.auditor_user.name}
            </Tag>
          )}
          {audit.season?.name && (
            <Tag color="magenta" style={{ fontSize: 13, padding: "2px 10px" }}>
              <strong>{t("audit.season", "Campaña")}:</strong>{" "}
              {audit.season.name}
            </Tag>
          )}
        </Space>
      )}

      {/* ── Segmentación top-level Resumen | Detalle ─────────────
          Sólo visible para admin: Resumen es una vista supervisor
          (KPIs agregados + pendientes + atajos). Los auditores y los
          auditores coordinadores van siempre a Datos (vista de
          trabajo) para evitar que la pestaña Resumen se interponga
          entre ellos y las parcelas / CPs / muestras. */}
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <Segmented
            size="large"
            value={activeSegment}
            onChange={(v) =>
              setActiveSegment(v as "resumen" | "datos")
            }
            options={[
              {
                label: t("audit.segment.summary", "Resumen"),
                value: "resumen",
                icon: <FundOutlined />,
              },
              {
                label: t("audit.segment.data", "Datos"),
                value: "datos",
                icon: <BlockOutlined />,
              },
            ]}
          />
        </div>
      )}

      {isAdmin && activeSegment === "resumen" && (
      <>
      {/* ── RESUMEN ─────────────────────────────────────────────
          Vista reducida: estado del aforo + mapa a ancho completo +
          KPIs y navegación rápida a Datos. Toda la ficha/contactos/
          submissions/historia vive en el segmento Datos. */}
      {/* Card de contexto unificada:
          Primero datos "quién/dónde" (productor, finca, auditor,
          empresa) → luego fechas → luego aforo-cert + croquis +
          observaciones. Todo lo que necesitas para situarte en un
          solo bloque, sin ir a Datos. */}
      {audit && (
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: "12px 16px" } }}
        >
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            colon
            style={{ marginBottom: 8 }}
          >
            <Descriptions.Item label="Productor">
              {audit.farm?.producer?.id ? (
                <a
                  href={`/producers/show/${audit.farm.producer.id}`}
                >
                  {audit.farm.producer.name ?? "—"}
                </a>
              ) : (
                audit.farm?.producer?.name ?? "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Finca">
              {audit.farm?.name ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Auditor">
              {audit.auditor_user?.name ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Empresa">
              {/* Serializer AuditDetailSerializer anida
                  `auditor_user` y `planning_group`; en cada uno la
                  empresa se expone como string plano
                  `audit_company_name` (no como objeto). Preferimos
                  la del auditor (petición del user) y caemos a la
                  de la planning si no está. */}
              {(audit.auditor_user as any)?.audit_company_name
                ?? (audit as any).planning_group?.audit_company_name
                ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Planificada">
              {(audit as any).planning_date
                ? dayjs((audit as any).planning_date).format("DD/MM/YYYY")
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha inicio">
              {audit.start_date
                ? dayjs(audit.start_date).format("DD/MM/YYYY")
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Duración">
              {/* Diferencia entre `finishing_time` y `start_time` =
                  tiempo real que el auditor pasó en campo. Los dos
                  son `TimeField` del mismo día (start_date). Más
                  útil que la fecha de finalización administrativa
                  (que puede ser días después). */}
              {audit.start_time && audit.finishing_time ? (() => {
                const start = dayjs(`2000-01-01 ${audit.start_time}`);
                const end = dayjs(`2000-01-01 ${audit.finishing_time}`);
                const mins = Math.max(end.diff(start, "minute"), 0);
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                return h > 0 ? `${h}h ${m}m` : `${m}m`;
              })() : "—"}
            </Descriptions.Item>
          </Descriptions>

          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {/* Estado del acta — el ciclo tiene dos fases bien
                diferenciadas (ver flujo acordado con el user):
                  · Pre-finalize: el acta aún no es definitiva. El
                    admin generará el acta final al pulsar "Cerrar y
                    finalizar" (regenera salvo que haya upload
                    manual). Mientras tanto: "Acta pendiente de
                    generar". Caso especial: productor no firmó via
                    móvil → el auditor puede descargar un draft para
                    enviar y re-subir firmado en papel.
                  · Finalize: la acta queda fija. Mostramos si está
                    firmada digital, firmada en papel o si se cerró
                    SIN firma del productor (caso raro pero legítimo
                    que hay que marcar en rojo). */}
            <Space wrap size="middle">
              <strong>Estado del acta:</strong>
              {(() => {
                const isFinalized = (audit as any).status_of_audit === 'finalized';
                const sigC = !!(audit as any).has_counterpart_signature;
                const uploadedManually = !!(audit as any).audit_act_uploaded_at;

                if (!isFinalized) {
                  if (uploadedManually) {
                    return (
                      <Tag icon={<CheckCircleOutlined />} color="blue">
                        Acta firmada cargada (lista para cierre)
                      </Tag>
                    );
                  }
                  if (!sigC) {
                    return (
                      <Tag icon={<WarningOutlined />} color="orange">
                        Pendiente: falta firma del productor
                      </Tag>
                    );
                  }
                  return (
                    <Tag icon={<WarningOutlined />} color="orange">
                      Acta pendiente de generar
                    </Tag>
                  );
                }

                // Finalizada:
                if (uploadedManually) {
                  return (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                      Firmada (recogida en papel)
                    </Tag>
                  );
                }
                if (sigC) {
                  return (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                      Firmada por ambas partes
                    </Tag>
                  );
                }
                return (
                  <Tag icon={<WarningOutlined />} color="red">
                    Finalizada SIN firma del productor
                  </Tag>
                );
              })()}
            </Space>
            <Space wrap size="middle">
              <strong>Aforo → Certification:</strong>
              {(audit as any).aforo_synced_at ? (
                <Tag icon={<CheckCircleOutlined />} color="green">
                  Enviado el{" "}
                  {dayjs((audit as any).aforo_synced_at).format("DD/MM/YYYY")}
                </Tag>
              ) : (
                <Tag icon={<WarningOutlined />} color="orange">
                  Pendiente de enviar
                </Tag>
              )}
              {/* Botón inline. Sólo visible para admin y cuando la
                  auditoría ya está finalizada — mismas pre-condiciones
                  que `AforoPushCard`. Reenvía si ya se envió. */}
              {isAdmin && (() => {
                const canPush =
                  (((audit as any).status_of_audit || "").toLowerCase() ===
                    "finalized") ||
                  /finaliz/i.test((audit as any).audit_state?.name ?? "");
                if (!canPush) return null;
                const already = !!(audit as any).aforo_synced_at;
                return (
                  <Button
                    size="small"
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    loading={sendingAforo}
                    onClick={handlePushAforoInline}
                  >
                    {already
                      ? t("audit.aforo.resend_btn", "Reenviar aforo")
                      : t("audit.aforo.send_btn", "Enviar aforo")}
                  </Button>
                );
              })()}
              <strong style={{ marginLeft: 12 }}>
                ¿Croquis coincide con la realidad?
              </strong>
              {(audit as any).map_is_showing_reality ? (
                <Tag icon={<CheckCircleOutlined />} color="green">
                  Sí
                </Tag>
              ) : (
                <Tag icon={<WarningOutlined />} color="orange">
                  No
                </Tag>
              )}
            </Space>
            {(aforoInlineError
              || ((audit as any).aforo_last_error || "").trim()) && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 4 }}
                message={
                  <span style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                    <strong>
                      {t(
                        "audit.aforo.last_error_short",
                        "Último error: ",
                      )}
                    </strong>
                    {aforoInlineError
                      || (audit as any).aforo_last_error}
                  </span>
                }
              />
            )}
            {!(audit as any).map_is_showing_reality
              && (audit as any).map_mismatch_reason && (
              <div style={{ fontSize: 13, color: "#595959" }}>
                <strong>Motivo del mismatch:</strong>{" "}
                {(audit as any).map_mismatch_reason}
              </div>
            )}
            <div style={{ fontSize: 13 }}>
              <strong>Observaciones / comentarios:</strong>{" "}
              {audit.comments ? (
                <span style={{ color: "#262626" }}>{audit.comments}</span>
              ) : (
                <span style={{ color: "#bfbfbf" }}>Sin comentarios</span>
              )}
            </div>
            {/* Trazabilidad del acta: si el admin la ha re-subido
                manualmente (versión firmada en papel) mostramos quién
                y cuándo. Si no hay info de upload, significa que el
                acta la generó el sistema (móvil o botón Generar). */}
            {(audit as any).audit_act_uploaded_at && (
              <div style={{ fontSize: 13 }}>
                <strong>Acta subida manualmente:</strong>{" "}
                <span style={{ color: "#262626" }}>
                  {dayjs((audit as any).audit_act_uploaded_at)
                    .format("DD/MM/YYYY HH:mm")}
                  {(audit as any).audit_act_uploaded_by_name && (
                    <> por {(audit as any).audit_act_uploaded_by_name}</>
                  )}
                </span>
              </div>
            )}
          </Space>
        </Card>
      )}

      {/* KPIs + navegación rápida — ANTES del mapa para que el
          mensaje de estado se lea primero y luego el mapa remate. */}
      <AuditSummaryTab
        audit={audit}
        auditPlots={auditPlots}
        cpCount={cpCount}
        acCount={acCount}
        photosCount={photosCount}
        onNavigateTab={(key) => {
          setActiveTab(key);
          setActiveSegment("datos");
        }}
      />

      {/* Mapa al final del Resumen (ancho completo). */}
      {audit?.id && (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <AuditMapCard
            ref={mapRef}
            auditId={audit.id}
            auditReference={audit.reference}
            producerName={audit.farm?.producer?.name}
            farmName={audit.farm?.name}
            mapHeight={`${Math.max(mapHeightPx, 420)}px`}
            refreshKey={mapRefreshKey}
            readOnly={isReadOnly}
            onEditSample={(sampleId) => {
              const target = (audit?.samples_summary ?? []).find(
                (s: any) => String(s.id) === sampleId,
              );
              if (target) {
                handleEditSample(target);
              } else {
                message.warning(
                  t(
                    "audit.sample.not_found_in_summary",
                    "No se encontró la muestra en la tabla — recarga la página.",
                  ),
                );
              }
            }}
          />
        </Card>
      )}
      </>
      )}

      {/* ── DATOS ───────────────────────────────────────────────
          Toda la información detallada: aforo-cert full, ficha
          "Información Básica", contactos, mapa (duplicado para que
          esté a mano aquí también), sub-tabs y sidebar.
          Para no-admin esta vista es la ÚNICA — el Segmented de
          arriba está oculto y entran directo aquí sin posibilidad
          de pasar al Resumen. */}
      {(activeSegment === "datos" || !isAdmin) && (
      <>
      {audit && isAdmin && (
        <AforoPushCard
          audit={audit as any}
          onRefresh={() => refetch()}
        />
      )}

      {/* TOP ROW — Basic info + Production stacked in the left column
          (40%), audit map filling the right column (60%). Cards use
          `height: 100%` + flex so both columns end up the same height. */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }} align="top">
        <Col xs={24} lg={10}>
          <div ref={leftColRef}>
      <Card
        title={t("audit.basic_info") || "Información Básica"}
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            // Locked for auditors/coordinators once the audit is
            // finalised — they have to ask an admin to re-open it,
            // which will regenerate the closure-report PDF + code.
            disabled={isReadOnly}
            onClick={() => {
              // Contexto de navegación viene ya resuelto (state o
              // sessionStorage) por el bloque effective* de arriba.
              navigate(`/audits/${audit?.id}/edit-info`, {
                state: { fromPlanningId, planningName },
              });
            }}
          >
            {t("audit.edit_info") || "Editar Información"}
          </Button>
        }
        bordered={false}
        style={{ marginBottom: 24 }}
      >
        <Descriptions
          column={1}
          bordered
          size="small"
        >
          {/* Referencia, Tipo, Estado y Auditor van en los tags
              superiores — no los duplicamos aquí. */}
          <Descriptions.Item label={t("audit.producer") || "Productor"}>
            {audit?.farm?.producer?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.farm") || "Finca"}>
            {audit?.farm?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.town") || "Municipio"}>
            {audit?.farm?.town?.name && audit?.farm?.town?.province?.name
              ? `${audit.farm.town.name} (${audit.farm.town.province.name})`
              : audit?.farm?.town?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.start_date") || "Fecha Realización"}>
            {audit?.start_date ? dayjs(audit.start_date).format("DD/MM/YYYY") : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.time_range_duration") || "Hora inicio - Hora fin (Duración)"}>
            {audit?.start_time && audit?.finishing_time ? (
              (() => {
                const start = dayjs(`2000-01-01 ${audit.start_time}`);
                const end = dayjs(`2000-01-01 ${audit.finishing_time}`);
                const duration = end.diff(start, 'minute');
                const hours = Math.floor(duration / 60);
                const minutes = duration % 60;
                return `${audit.start_time} - ${audit.finishing_time} (${hours}h ${minutes}m)`;
              })()
            ) : audit?.start_time || audit?.finishing_time || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.farm_surface") || "Superficie Finca"}>
            {audit?.farm?.surface ? `${formatNumber(audit.farm.surface, 2)} ha` : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.audited_surface") || "Superficie Auditada"}>
            {(() => {
              const totalSurface = audit?.plots_visited?.reduce((sum, plot) => sum + (plot.surface || 0), 0) || 0;
              return totalSurface > 0 ? `${formatNumber(totalSurface, 2)} ha` : "-";
            })()}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.samples_to_take") || "Muestras a Tomar"} span={1}>
            {audit?.samples_to_take ?? "-"} ({audit?.samples_to_process ?? 0} {t("audit.samples_processed") || "procesadas"})
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.total_samples_taken") || "Total Muestras Tomadas"} span={1}>
            {audit?.samples_summary?.length ?? 0}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("audit.remark") || "Observaciones o Comentarios"}
            span={1}
            contentStyle={{ whiteSpace: 'pre-wrap' }}
          >
            {audit?.comments || "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* PR5 — Validación de superficie per-variedad. Solo admin la
          ve (el componente se auto-oculta si isAdmin=false). */}
      {audit?.id && (
        <SurfaceValidationCard scope="audit" id={audit.id} isAdmin={isAdmin} />
      )}

      {/* Production Information Card — stacked below Basic Info so
          the two highest-priority blocks share the left column. */}
      <Card
        title={t("audit.production_info") || "Información de Producción"}
        bordered={false}
        style={{ marginBottom: 0 }}
      >
        <Descriptions
          column={1}
          bordered
          size="small"
        >
          <Descriptions.Item label={t("audit.packer") || "Etiquetador"}>
            {audit?.packers && audit.packers.length > 0
              ? audit.packers.map((packer: any) => packer.name).join(", ")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.exporter") || "Exportador"}>
            {audit?.exporters && audit.exporters.length > 0
              ? audit.exporters.map((exporter: any) => exporter.name).join(", ")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.estimated_production_producer") || "Producción Estimada Productor"}>
            {audit?.stimated_production ? `${formatNumber(audit.stimated_production, 2)} kg` : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.estimated_production_auditor") || "Producción Estimada Auditor"}>
            {(() => {
              const totalKgPlot = audit?.plots_visited?.reduce((sum, plot) => sum + (plot.kg_plot || 0), 0) || 0;
              return totalKgPlot > 0 ? `${formatNumber(totalKgPlot, 2)} kg` : "-";
            })()}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.harvest_date") || "Fecha Estimada de Corta"}>
            {audit?.harvest_date ? dayjs(audit.harvest_date).format("DD/MM/YYYY") : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.decrease_reason") || "Motivo de Merma"}>
            {audit?.reason_for_decrease && audit.reason_for_decrease.length > 0
              ? audit.reason_for_decrease.map((reason: any) => reason.name).join(", ")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.decrease_percentage") || "% de Merma"}>
            {audit?.decrease_percentage ? `${audit.decrease_percentage}%` : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.is_under_net") || "Finca Mallada"}>
            {audit?.is_under_net ? (
              audit.is_under_net === 'yes' ? 'Sí' :
              audit.is_under_net === 'no' ? 'No' :
              audit.is_under_net === 'partial' ? 'Parcial' :
              audit.is_under_net
            ) : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={t("audit.production_type") || "Tipo de Producción"}>
            {audit?.production_type ? (
              audit.production_type === 'convencional' ? 'Convencional' :
              audit.production_type === 'transformacion' ? 'En Conversión' :
              audit.production_type === 'eco' ? 'ECO' :
              audit.production_type
            ) : "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>
          </div>
        </Col>
        <Col xs={24} lg={14}>
          {/* Contacts above the map, same width. Cards in a single
              horizontal scroll row so the height stays predictable
              regardless of contact count. */}
          {audit?.farm?.producer?.id && (
            <div ref={contactsRef}>
              <ContactChainsPanel
                auditId={audit.id}
                auditReference={audit.reference}
                producerId={audit.farm.producer.id}
                producerName={audit.farm.producer.name}
                farmId={audit.farm?.id ?? null}
                farmName={audit.farm?.name ?? null}
                isAdmin={isAdmin}
                layout="horizontal"
                readOnly={isReadOnly}
              />
            </div>
          )}
          {audit?.id && (
            <AuditMapCard
              ref={mapRef}
              auditId={audit.id}
              auditReference={audit.reference}
              producerName={audit.farm?.producer?.name}
              farmName={audit.farm?.name}
              mapHeight={`${mapHeightPx}px`}
              refreshKey={mapRefreshKey}
              readOnly={isReadOnly}
              onEditSample={(sampleId) => {
                // El mapa nos da el UUID de la muestra; buscamos el
                // objeto completo en `samples_summary` (que ya trae
                // los datos que el modal necesita) y abrimos el
                // modal de edición.
                const target = (audit?.samples_summary ?? []).find(
                  (s: any) => String(s.id) === sampleId,
                );
                if (target) {
                  handleEditSample(target);
                } else {
                  message.warning(
                    t(
                      "audit.sample.not_found_in_summary",
                      "No se encontró la muestra en la tabla — recarga la página.",
                    ),
                  );
                }
              }}
            />
          )}
        </Col>
      </Row>

      {/* SECOND ROW — Detail tabs on the left (16/24), Contacts and
          Farm history stacked on the right (8/24). The sidebar gives
          the auditor permanent visibility on phones and the audit
          history while they navigate the tables. */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
      {/* DETAIL TABS — Parcelas / Puntos de control / Variedades
          colindantes / Muestras / Galería. Default lands on Parcelas
          (most common starting point once the auditor is in detail). */}
      <Card
        bordered={false}
        styles={{ body: { padding: 0 } }}
        style={{ marginBottom: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          tabBarStyle={{
            paddingLeft: 16,
            paddingRight: 16,
            marginBottom: 0,
          }}
          items={[
            {
              key: "parcelas",
              label: (
                <Space>
                  <BlockOutlined />
                  {t("audit.tabs.plots", "Parcelas")}
                  <Badge
                    count={auditPlots.length}
                    showZero
                    style={{ backgroundColor: "#ff8c00" }}
                  />
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
      <Card
        title={t("audit.plots_visited") || "Parcelas Visitadas"}
        extra={
          <Space size={8}>
            {/* Admin-only: re-ejecuta la precarga de AuditPlots
                desde las PlotReal de la finca. Útil cuando la
                auditoría se creó antes de que existieran canónicas
                (el signal post_save corrió pero no precargó nada).
                Idempotente. Bloqueado en finalizadas y completas
                (las completas se crean desde cero por diseño). */}
            {isAdmin && !isReadOnly && !isCompleteAudit && (
              <Tooltip
                title={t(
                  "audit.precharge_tooltip",
                  "Recargar AuditPlots desde las parcelas canónicas actuales de la finca. Útil si la auditoría se creó antes de materializar el histórico canónico.",
                )}
              >
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={handlePrechargePlots}
                  loading={prechargingPlots}
                >
                  {t("audit.precharge_button", "Precargar parcelas")}
                </Button>
              </Tooltip>
            )}
            {/* Fusionar parcelas dobladas — admin/coord, solo cuando
                la auditoría no está bloqueada. En modo merge oculta
                el resto de acciones para no confundir. */}
            {(isAdmin || isCoordinator) && !isReadOnly && !mergeMode && (
              <Button
                icon={<MergeCellsOutlined />}
                onClick={handleStartMerge}
                disabled={(auditPlots?.length ?? 0) < 2}
              >
                Fusionar
              </Button>
            )}
            {mergeMode && (
              <>
                <Button onClick={handleCancelMerge}>Cancelar</Button>
                <Button
                  type="primary"
                  danger
                  loading={mergeSubmitting}
                  disabled={mergeSelectedIds.length < 2}
                  onClick={handleConfirmMerge}
                >
                  Fusionar {mergeSelectedIds.length} parcelas
                </Button>
              </>
            )}
            {!mergeMode && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreatePlot}
              // Solo bloqueado en finalizadas. En cualquier otro tipo
              // (incluido aforo) el auditor puede crear parcelas — al
              // cerrar la auditoría la nueva AuditPlot se materializa
              // como PlotReal canónica (ver audit_close.py).
              disabled={isReadOnly}
            >
              {t("plot.create_button") || "Crear Parcela"}
            </Button>
            )}
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 0 }}
        loading={loadingPlots}
      >
        {isAforoAudit && !isReadOnly && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t(
              "audit.aforo_plots_note_title",
              "Auditoría de aforo",
            )}
            description={t(
              "audit.aforo_plots_note_body",
              "Las parcelas se han pre-cargado desde el estado canónico de la finca. Edita cada parcela para registrar su aforo (kg + bloques). Si detectas un dato canónico erróneo (marco, superficie, patrón…), corrígelo: la auditoría al cerrarse registrará el cambio en el histórico de la parcela.",
            )}
          />
        )}
        {/* Green row when the auditor has saved the review wizard
            at least once for this plot (reviewed_at != null). */}
        <style>{`
          .audit-plots-table .plot-reviewed-row td {
            background-color: #f6ffed !important;
          }
        `}</style>
        {mergeMode && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Modo fusión activo"
            description={
              <>
                Marca al menos <strong>2 parcelas</strong>. La primera será
                el <strong>target</strong> (conserva identidad, geometría y
                aforo); las demás se absorben — sus plantaciones pasan al
                target y las filas absorbidas se eliminan. Fotos, bloques y
                comentarios de las absorbidas <strong>se descartan</strong>.
              </>
            }
          />
        )}
        <Table<AuditPlot>
          className="audit-plots-table planning-audits-table"
          size="small"
          dataSource={auditPlots}
          columns={plotColumns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 735 }}
          rowSelection={
            mergeMode
              ? {
                  type: "checkbox",
                  selectedRowKeys: mergeSelectedIds.map((n) => String(n)),
                  onChange: (keys) =>
                    setMergeSelectedIds(keys.map((k) => Number(k))),
                  // Todas las parcelas de la auditoría son
                  // seleccionables. Antes bloqueábamos por
                  // `plot_real_closed_in_audit` pero ese campo indica
                  // "el audit que cerró la parcela canónica en el
                  // pasado" — no es equivalente a "no fusionable". La
                  // validación real la hace `mergePlots` en el backend
                  // (que rechaza combinaciones inválidas con 400) y el
                  // error se muestra en el modal.
                }
              : undefined
          }
          rowClassName={(record) =>
            (record as any).reviewed_at ? "plot-reviewed-row" : ""
          }
          locale={{
            emptyText: (
              <Empty
                description={
                  t("audit.no_plots_visited") || "No hay parcelas visitadas"
                }
              />
            ),
          }}
          summary={(pageData) => {
            let totalSurface = 0;
            let totalPlants = 0;
            let totalKgPlot = 0;

            pageData.forEach(({ surface, plants, kg_plot }) => {
              totalSurface += surface || 0;
              totalPlants += plants || 0;
              totalKgPlot += kg_plot || 0;
            });

            return (
              <>
                <style>{`
                  .plot-summary-row td {
                    font-weight: bold !important;
                    background-color: #fafafa !important;
                  }
                `}</style>
                <Table.Summary.Row className="plot-summary-row">
                  <Table.Summary.Cell index={0}>{t("audit.total") || "TOTAL"}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    {formatNumber(totalSurface, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {formatNumber(totalPlants)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {formatNumber(totalKgPlot, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}></Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      </Card>
                </div>
              ),
            },
            {
              key: "puntos-control",
              // Mount the section eagerly so its onCountChange
              // callback fires on first load and the tab badge
              // appears without waiting for the user to click.
              forceRender: true,
              label: (
                <Space>
                  <AlertOutlined />
                  {t("audit.tabs.control_points", "Puntos de control")}
                  {cpCount !== null && (
                    <Badge
                      count={cpCount}
                      showZero
                      style={{ backgroundColor: "#ff4d4f" }}
                    />
                  )}
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {audit?.id && audit?.farm?.id && (
                    <ControlPointsSection
                      auditId={audit.id}
                      farmId={audit.farm.id}
                      onRefresh={() => {
                        refetch();
                        bumpMap();
                      }}
                      onCountChange={setCpCount}
                      readOnly={isCpReadOnly}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "variedades",
              // Eager mount: see comment on "puntos-control".
              forceRender: true,
              label: (
                <Space>
                  <EnvironmentOutlined />
                  {t("audit.tabs.adjacent_crops", "Variedades colindantes")}
                  {acCount !== null && (
                    <Badge
                      count={acCount}
                      showZero
                      style={{ backgroundColor: "#722ed1" }}
                    />
                  )}
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {audit?.id && audit?.farm?.id && (
                    <AdjacentCropsSection
                      auditId={audit.id}
                      farmId={audit.farm.id}
                      onRefresh={() => {
                        refetch();
                        bumpMap();
                      }}
                      onCountChange={setAcCount}
                      readOnly={isAdjacentCropReadOnly}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "muestras",
              label: (
                <Space>
                  <ExperimentOutlined />
                  {t("audit.tabs.samples", "Muestras")}
                  <Badge
                    count={audit?.samples_summary?.length ?? 0}
                    showZero
                    style={{ backgroundColor: "#1677ff" }}
                  />
                </Space>
              ),
              children: (
                <div id="audit-samples-card" style={{ padding: 16 }}>
                  <Card
                    title={t("audit.samples_summary") || "Resumen de Muestras"}
                    extra={
                      <Space size="small">
                        {/* Excel con lab_code, resultado, parcela,
                            waypoint y notas. Útil para reporting rápido
                            al productor o al laboratorio sin abrir la BD. */}
                        <Button
                          icon={<FileExcelOutlined />}
                          loading={exportingSamplesXlsx}
                          onClick={exportSamplesXlsx}
                          disabled={
                            !audit?.samples_summary
                            || audit.samples_summary.length === 0
                          }
                          title={t(
                            "audit.samples.export_xlsx_tooltip",
                            "Descargar Excel con las muestras (código, resultado, parcela, waypoint, notas)",
                          )}
                        >
                          {t("audit.samples.export_xlsx", "Exportar Excel")}
                        </Button>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => setBatchEditorVisible(true)}
                          disabled={isSamplesReadOnly}
                          title={
                            isReadOnly && isAdmin
                              ? t(
                                  "audit.sample.admin_create_locked",
                                  "Añadir muestras (auditoría cerrada — sólo admin)",
                                )
                              : undefined
                          }
                        >
                          {t("sample.batch.cta", "Crear muestras")}
                        </Button>
                      </Space>
                    }
                    bordered={false}
                    style={{ marginBottom: 0 }}
                  >
                    {audit?.samples_summary && audit.samples_summary.length > 0 ? (
                      <>
                        <Space
                          style={{ marginBottom: 12, width: "100%" }}
                          size="small"
                          wrap
                        >
                          <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder={t(
                              "audit.samples.search_placeholder",
                              "Buscar por código, referencia, waypoint o parcela",
                            )}
                            value={sampleSearchText}
                            onChange={(e) => setSampleSearchText(e.target.value)}
                            style={{ width: 320 }}
                          />
                          <Select
                            allowClear
                            placeholder={t(
                              "audit.samples.filter_result",
                              "Filtrar por resultado",
                            )}
                            value={sampleResultFilter}
                            onChange={(v) => setSampleResultFilter(v)}
                            style={{ width: 200 }}
                            options={[
                              { label: t("samples.result_pendiente", "Pendiente"), value: "PENDIENTE" },
                              { label: t("samples.result_tango", "Tango"), value: "TANGO" },
                              { label: t("samples.result_no_tango", "No Tango"), value: "NO_TANGO" },
                              { label: t("samples.result_nc", "No Concluyente"), value: "NC" },
                              { label: t("samples.result_na", "No Analizada"), value: "NA" },
                              { label: t("samples.result_mixed", "Divergente"), value: "MIXED" },
                              { label: t("samples.result_other", "Otra variedad"), value: "OTHER_VARIETY" },
                            ]}
                          />
                        </Space>
                      <Table<Sample>
                        className="planning-audits-table"
                        size="small"
                        dataSource={filteredSamples}
                        columns={sampleColumns}
                        rowKey="id"
                        // Local pagination: muestras suelen ser
                        // decenas/centenas por auditoría — paginar
                        // localmente evita scroll infinito y deja la
                        // fila de resumen anclada al pie de página.
                        pagination={{
                          defaultPageSize: 25,
                          pageSizeOptions: [10, 25, 50, 100],
                          showSizeChanger: true,
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} ${
                              t("common.of", "de")
                            } ${total}`,
                          size: "small",
                        }}
                        rowClassName={getRowClassName}
                        summary={() => {
                          // Fila resumen: total global + total tras
                          // aplicar filtros (buscador / resultado).
                          // El auditor ve rápido cuánto ha filtrado.
                          const totalSamples =
                            audit?.samples_summary?.length ?? 0;
                          const shown = filteredSamples.length;
                          const isFiltered = shown !== totalSamples;
                          return (
                            <>
                              <style>{`
                                .sample-summary-row td {
                                  font-weight: bold !important;
                                  background-color: #fafafa !important;
                                }
                              `}</style>
                              <Table.Summary.Row className="sample-summary-row">
                                <Table.Summary.Cell index={0} colSpan={5}>
                                  {t("audit.total_samples") || "TOTAL MUESTRAS"}: {totalSamples}
                                  {isFiltered ? ` · ${t("audit.samples.filtered", "Filtradas")}: ${shown}` : ""}
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </>
                          );
                        }}
                      />
                      </>
                    ) : (
                      <Empty
                        description={
                          t("audit.no_samples") || "No hay muestras registradas"
                        }
                      />
                    )}
                  </Card>
                </div>
              ),
            },
            {
              key: "incidencias",
              // Eager mount: see comment on "puntos-control".
              forceRender: true,
              label: (
                <Space>
                  <WarningOutlined />
                  {t("audit.tabs.incidences", "Incidencias")}
                  {incidencesCount !== null && (
                    <Badge
                      count={incidencesCount}
                      showZero
                      style={{ backgroundColor: "#faad14" }}
                    />
                  )}
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {audit?.id && (
                    <AuditIncidencesSection
                      auditId={audit.id}
                      onCreateIncidence={() => setIncidenceModalOpen(true)}
                      refreshKey={incidencesRefreshKey}
                      onCountChange={setIncidencesCount}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "fotos",
              // Eager mount: see comment on "puntos-control".
              forceRender: true,
              label: (
                <Space>
                  <CameraOutlined />
                  {t("audit.tabs.photos", "Galería de fotos")}
                  {photosCount !== null && (
                    <Badge
                      count={photosCount}
                      showZero
                      style={{ backgroundColor: "#13c2c2" }}
                    />
                  )}
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {audit?.id && (
                    <AuditPhotoGallery
                      auditId={audit.id}
                      onUploadClick={handleUploadPhotos}
                      uploadDisabled={auditPlots.length === 0}
                      refreshKey={photosRefreshKey}
                      onCountChange={setPhotosCount}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
        </Col>
        <Col xs={24} lg={8}>
          {/* SIDEBAR */}
          {/* Cierres del auditor: se muestra siempre que haya
               submissions (o mientras se cargan). Auditor + admin lo
               ven — para el admin es la herramienta principal de
               validación; para el auditor es la prueba histórica
               que puede referenciar si hay reclamación. */}
          {audit?.id && (
            <AuditSubmissionsPanel
              auditId={audit.id}
              refreshKey={submissionsRefreshKey}
              hideWhenEmpty
            />
          )}
          {/* Farm history. Contacts moved up to the top row above
              the map. */}
          {audit?.id && (
            <FarmHistoryCard
              auditId={audit.id}
              farmName={audit.farm?.name}
              refreshKey={historyRefreshKey}
            />
          )}
        </Col>
      </Row>
      </>
      )}

      {/* Cancel Audit Modal — admin-only, gated by `canCancelAudit`
          on the dropdown. Reuses the planning-detail component;
          posts to /audits/{id}/cancel/ and refreshes the audit so
          the state tag flips to "Cancelada". */}
      {audit && (
        <CancelAuditModal
          visible={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          audit={audit as any}
          onSuccess={() => {
            refetch();
            bumpHistory();
          }}
        />
      )}

      {/* Create Incidence Modal — opens from the Acciones dropdown
          or from the Incidencias tab. After a successful create we
          stay on the audit page and refresh the incidences tab so
          the new row appears without losing context. */}
      {audit?.id && (
        <CreateIncidenceModal
          open={incidenceModalOpen}
          auditId={audit.id}
          onClose={() => setIncidenceModalOpen(false)}
          onCreated={() => {
            setIncidencesRefreshKey((n) => n + 1);
            setActiveTab("incidencias");
          }}
        />
      )}

      {/* Upload Audit Act Modal */}
      {audit?.id && (
        <UploadAuditActModal
          visible={uploadModalVisible}
          onClose={() => setUploadModalVisible(false)}
          auditId={audit.id}
          hasExistingAct={!!audit?.has_audit_act}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Upload Audit Data Modal */}
      {audit?.id && audit?.audit_type?.id && (
        <UploadAuditDataModal
          visible={uploadDataModalVisible}
          onClose={() => setUploadDataModalVisible(false)}
          auditId={audit.id}
          auditType={audit.audit_type.id}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Row-level "Dar de baja parcela" modal — same flow as the
          close modal on CP / colindantes. */}
      <Modal
        title={t("plot.close_modal_title", "Dar de baja parcela")}
        open={plotCloseModalOpen}
        onCancel={() => (plotClosing ? undefined : setPlotCloseModalOpen(false))}
        onOk={handleConfirmClosePlot}
        confirmLoading={plotClosing}
        okText={t("plot.close", "Dar de baja")}
        cancelText={t("common.cancel", "Cancelar")}
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Typography.Paragraph>
          {t(
            "plot.close_body",
            "Marca la parcela {{name}} como cerrada (cortada / arrancada / desaparecida). Dejará de aparecer en futuras auditorías; en ésta queda marcada en rojo y en las anteriores se mantiene como estaba.",
            { name: plotForClose?.name ?? "" },
          )}
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={plotCloseReason}
          onChange={(e) => setPlotCloseReason(e.target.value)}
          placeholder={t(
            "plot.close_reason_placeholder",
            "Motivo (p. ej. cortada, fusionada con otra…)",
          )}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Bulk close modal — abierto desde el aviso "No se puede
          finalizar todavía" cuando lo único pendiente son parcelas
          arrancadas. Aplica el mismo motivo a todas. */}
      <Modal
        title={t(
          "plot.bulk_close_title",
          "Dar de baja parcelas pendientes como arrancadas",
        )}
        open={bulkCloseModalOpen}
        onCancel={() =>
          bulkClosing ? undefined : setBulkCloseModalOpen(false)
        }
        onOk={handleConfirmBulkClose}
        confirmLoading={bulkClosing}
        okText={t(
          "plot.bulk_close_ok",
          "Dar de baja las {{n}} parcelas",
          { n: bulkClosePlots.length },
        )}
        cancelText={t("common.cancel", "Cancelar")}
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Typography.Paragraph>
          {t(
            "plot.bulk_close_body",
            "Se marcarán como cerradas las siguientes parcelas con el mismo motivo. Quedarán como dadas de baja en este audit y dejarán de aparecer en futuras auditorías de la finca.",
          )}
        </Typography.Paragraph>
        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
          {bulkClosePlots.slice(0, 10).map((p) => (
            <li key={p.id}>
              <Typography.Text>{(p as any).name || `#${p.id}`}</Typography.Text>
            </li>
          ))}
          {bulkClosePlots.length > 10 && (
            <li>
              <Typography.Text type="secondary">
                {t("common.and_more", "y {{n}} más", {
                  n: bulkClosePlots.length - 10,
                })}
              </Typography.Text>
            </li>
          )}
        </ul>
        <Input.TextArea
          rows={3}
          value={bulkCloseReason}
          onChange={(e) => setBulkCloseReason(e.target.value)}
          placeholder={t(
            "plot.bulk_close_reason_placeholder",
            "Motivo común (p. ej. arrancadas, finca abandonada…)",
          )}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Photo Upload Modal */}
      {audit?.id && (
        <PhotoUploadModal
          visible={photoUploadModalVisible}
          onClose={() => setPhotoUploadModalVisible(false)}
          auditId={audit.id}
          plots={auditPlots}
          onSuccess={handlePhotoUploadSuccess}
        />
      )}

      {/* Sample Batch Editor — primary "Create samples" flow. */}
      {audit?.id && (
        <SampleBatchEditor
          visible={batchEditorVisible}
          auditId={audit.id}
          auditPlots={auditPlots}
          controlPoints={controlPoints}
          loadingPlots={loadingPlots}
          loadingControlPoints={loadingControlPoints}
          onClose={() => setBatchEditorVisible(false)}
          onSuccess={() => {
            refetch();
            bumpMap();
            // Bulk-created samples change the per-audit sample count,
            // which the farm history card surfaces. Refresh it too.
            bumpHistory();
          }}
        />
      )}

      {/* Closure-report progress modal — stays open until every
          stage resolves. Non-dismissable on purpose: the map capture
          + dynamic jspdf import can take several seconds and we
          don't want the user to think nothing is happening. */}
      <Modal
        open={generatingReport}
        closable={false}
        maskClosable={false}
        footer={null}
        centered
        width={420}
        title={t("audit.report_generating_title", "Generando acta de cierre")}
      >
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%", padding: "8px 4px" }}
        >
          <div style={{ textAlign: "center" }}>
            <Spin size="large" />
          </div>
          <Steps
            direction="vertical"
            size="small"
            current={
              reportStage === "capturing"
                ? 0
                : reportStage === "fetching"
                  ? 1
                  : reportStage === "building"
                    ? 2
                    : reportStage === "downloading"
                      ? 3
                      : reportStage === "finalizing"
                        ? 4
                        : 0
            }
            items={[
              {
                title: t("audit.report_step_map", "Capturando mapa"),
                description: t(
                  "audit.report_step_map_desc",
                  "Tiles satélite + etiquetas de cada elemento",
                ),
              },
              {
                title: t(
                  "audit.report_step_data",
                  "Cargando datos de la auditoría",
                ),
                description: t(
                  "audit.report_step_data_desc",
                  "Puntos de control, variedades colindantes y muestras",
                ),
              },
              {
                title: t("audit.report_step_build", "Construyendo el PDF"),
                description: t(
                  "audit.report_step_build_desc",
                  "Información básica, parcelas y resúmenes",
                ),
              },
              {
                title: t(
                  "audit.report_step_download",
                  "Preparando descarga",
                ),
              },
              {
                title: t(
                  "audit.report_step_finalize",
                  "Cerrando la auditoría",
                ),
                description: t(
                  "audit.report_step_finalize_desc",
                  "Marcando como finalizada en el servidor",
                ),
              },
            ]}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "audit.report_wait_hint",
              "Esta operación puede tardar unos segundos. No cierres la ventana.",
            )}
          </Typography.Text>
        </Space>
      </Modal>

      {/* Photo Gallery Modal */}
      {selectedPlot && (
        <PhotoGalleryModal
          visible={photoGalleryModalVisible}
          onClose={() => {
            setPhotoGalleryModalVisible(false);
            setSelectedPlot(null);
          }}
          entityId={selectedPlot.id}
          entityName={selectedPlot.name}
          entityType="plot"
          onPhotoDeleted={handlePhotoDeleted}
        />
      )}

      {/* Legacy Sample Edit Modal (admin only). Sirve para editar
          códigos y nombres de waypoint de muestras históricas —
          `AuditLaboratorySample` — sin exponer los campos de resultado
          (state, cq, csic) que se gestionan por otra vía. */}
      <Modal
        title={t(
          "audit.sample.legacy_modal_title",
          "Editar muestra histórica (admin)",
        )}
        open={legacyEditVisible}
        onOk={() => legacyForm.submit()}
        onCancel={() => {
          setLegacyEditVisible(false);
          legacyForm.resetFields();
          setLegacyEditingSample(null);
        }}
        confirmLoading={savingLegacy}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
      >
        <Form form={legacyForm} layout="vertical" onFinish={handleSaveLegacySample}>
          <Form.Item
            label={t("audit.sample.lab_code", "Código Laboratorio")}
            name="code"
            rules={[{ required: true, message: t(
              "audit.sample.lab_code_required",
              "El código de laboratorio es obligatorio",
            ) }]}
          >
            <Input
              maxLength={200}
              onChange={(e) => {
                // Los códigos siempre en mayúsculas — el modelo
                // legacy sí acepta minúsculas pero la convención es
                // uniformar antes de guardar.
                const v = e.target.value.toUpperCase();
                if (v !== e.target.value) {
                  legacyForm.setFieldValue("code", v);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={t("audit.sample.plot_name", "Parcela / Referencia")}
            name="plot_name"
          >
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item
            label={t("audit.sample.waypoint", "Waypoint")}
            name="position_waypoint"
            extra={t(
              "audit.sample.waypoint_help",
              "Nombre del waypoint en el GPS. Al cambiarlo, se limpia la geometría asociada; usa el botón «Buscar geometría» para volver a asociarla desde los archivos de la planificación.",
            )}
          >
            <Input maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sample Create/Edit Modal */}
      <Modal
        title={editingSample ? "Editar Muestra" : "Crear Muestra"}
        open={sampleModalVisible}
        onOk={() => sampleForm.submit()}
        onCancel={() => {
          setSampleModalVisible(false);
          sampleForm.resetFields();
          setEditingSample(null);
        }}
        confirmLoading={isSavingSample}
        okText={
          editingSample
            ? t("common.update", "Actualizar")
            : t("common.create", "Crear")
        }
        cancelText={t("common.cancel", "Cancelar")}
      >
        <Form
          form={sampleForm}
          layout="vertical"
          onFinish={handleSaveSample}
        >
          {/* 1. Código Laboratorio */}
          <Form.Item
            label={t("audit.sample.lab_code", "Código Laboratorio")}
            name="code"
            rules={[{
              required: true,
              message: t(
                "audit.sample.lab_code_required",
                "El código de laboratorio es obligatorio",
              ),
            }]}
          >
            <Input
              placeholder={t(
                "audit.sample.lab_code_placeholder",
                "Ingrese el código de laboratorio",
              )}
            />
          </Form.Item>

          {/* 2. Número Waypoint */}
          <Form.Item
            label={t("audit.sample.waypoint_number", "Número Waypoint")}
            name="waypoint"
            rules={[{
              required: true,
              message: t(
                "audit.sample.waypoint_required",
                "El número de waypoint es obligatorio",
              ),
            }]}
          >
            <Input
              placeholder={t(
                "audit.sample.waypoint_placeholder",
                "Ingrese el número de waypoint",
              )}
            />
          </Form.Item>

          {/* 3. ¿Es Punto de Control? */}
          <Form.Item
            label={t(
              "audit.sample.is_control_point",
              "¿Es Punto de Control?",
            )}
            name="is_control_point"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              onChange={(checked) => {
                setIsControlPoint(checked);
                // Clear the unrelated field so we don't submit stale
                // values when the user toggles between modes.
                if (checked) {
                  sampleForm.setFieldValue("audit_plot", undefined);
                } else {
                  sampleForm.setFieldValue("control_point", undefined);
                }
              }}
            />
          </Form.Item>

          {/* 4. Parcela (si NO es punto de control) — selector con las
              parcelas creadas en esta auditoría. El value es el id de
              la parcela; el backend lo guarda como FK `audit_plot`. */}
          {!isControlPoint && (
            <Form.Item
              label={t("audit.sample.plot", "Parcela")}
              name="audit_plot"
              rules={[{
                required: true,
                message: t(
                  "audit.sample.plot_required",
                  "Debe seleccionar una parcela",
                ),
              }]}
              extra={
                auditPlots.length === 0 && !loadingPlots
                  ? t(
                      "audit.sample.no_plots_hint",
                      "No hay parcelas creadas en esta auditoría. Crea una parcela antes de añadir muestras.",
                    )
                  : undefined
              }
            >
              <Select
                placeholder={t(
                  "audit.sample.plot_placeholder",
                  "Seleccione una parcela",
                )}
                showSearch
                allowClear
                loading={loadingPlots}
                disabled={auditPlots.length === 0 && !loadingPlots}
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                dropdownStyle={{ zIndex: 10000 }}
                options={auditPlots.map((plot) => ({
                  value: plot.id,
                  label: plot.name,
                }))}
              />
            </Form.Item>
          )}

          {/* 5. Notas — texto libre para "fila 5, árbol 12" u observaciones
              que ayuden a re-localizar la muestra. Se guarda en Sample.notes. */}
          <Form.Item
            label={t("audit.sample.notes", "Notas")}
            name="notes"
            extra={t(
              "audit.sample.notes_extra",
              "Opcional. Útil para anotar fila/árbol o cualquier observación sobre la muestra.",
            )}
          >
            <Input.TextArea
              rows={3}
              maxLength={2000}
              showCount
              placeholder={t(
                "audit.sample.notes_placeholder",
                "Ej.: fila 5 · árbol 12 desde el norte",
              )}
            />
          </Form.Item>

          {/* 4. Punto de Control (si ES punto de control) */}
          {isControlPoint && (
            <Form.Item
              label={t("audit.sample.control_point", "Punto de Control")}
              name="control_point"
              rules={[{
                required: true,
                message: t(
                  "audit.sample.control_point_required",
                  "Debe seleccionar un punto de control",
                ),
              }]}
              extra={
                controlPoints.length === 0 && !loadingControlPoints
                  ? t(
                      "audit.sample.no_control_points_hint",
                      "No hay puntos de control en esta auditoría. Crea uno desde el mapa o desde el listado.",
                    )
                  : undefined
              }
            >
              <Select
                placeholder={t(
                  "audit.sample.control_point_placeholder",
                  "Seleccione un punto de control",
                )}
                showSearch
                allowClear
                loading={loadingControlPoints}
                disabled={controlPoints.length === 0 && !loadingControlPoints}
                optionFilterProp="label"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                dropdownStyle={{ zIndex: 10000 }}
                options={controlPoints.map((cp: any) => ({
                  value: cp.id,
                  label: cp.name,
                }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Modal — Reabrir auditoría (admin) */}
      <Modal
        title={t("audit.reopen_modal_title", "Reabrir auditoría")}
        open={reopenModalOpen}
        onCancel={() => setReopenModalOpen(false)}
        onOk={handleReopenAudit}
        okText={t("audit.reopen_confirm", "Reabrir")}
        okButtonProps={{ danger: true, loading: reopening }}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message={t(
            "audit.reopen_warning_title",
            "Vas a reabrir una auditoría cerrada",
          )}
          description={t(
            "audit.reopen_warning_body",
            "La auditoría dejará de estar bloqueada para edición. Deberás cerrarla de nuevo cuando termines los cambios.",
          )}
          style={{ marginBottom: 12 }}
        />
        {(audit as any)?.aforo_synced_at && (
          <Alert
            type="info"
            showIcon
            message={t(
              "audit.reopen_aforo_hint_title",
              "El aforo ya está enviado a Certification",
            )}
            description={t(
              "audit.reopen_aforo_hint_body",
              "Si modificas datos que afecten al aforo, tras cerrarla de nuevo tendrás que pulsar 'Reenviar aforo' en el card de sincronización.",
            )}
            style={{ marginBottom: 12 }}
          />
        )}
        <div>
          <label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
            {t(
              "audit.reopen_reason_label",
              "Motivo (opcional — queda registrado en el log de reaperturas)",
            )}
          </label>
          <Input.TextArea
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            rows={3}
            placeholder={t(
              "audit.reopen_reason_placeholder",
              "Ej.: corregir kg_plot de P3 tras revisión con el productor",
            )}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </Show>
  );
};
